import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useAuth } from './auth/AuthContext';
import { API_BASE } from './apiConfig';
import { MAIN_SITE_EMBED_URL } from './utils/embedIntegration';

const STATUS_OPTIONS = ['UPCOMING', 'LIVE', 'FINAL', 'POSTPONED', 'CANCELLED'];
const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_FILTER_DAYS_BACK = 7;
const ADMIN_PAGE = {
  DASHBOARD: 'dashboard',
  GAMES: 'games',
  LOGOS: 'logos'
};
const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DRAFT_FIELD_ID_PREFIX = 'draft-field';
const PREVIEW_FEED_MESSAGE_EVENT = 'SLB_WIDGET_FEED_UPDATE';

const toDomSafeToken = (value) => String(value || '').replace(/[^a-zA-Z0-9_-]/g, '_');

const buildDraftFieldId = (draftKey, field) => (
  `${DRAFT_FIELD_ID_PREFIX}-${toDomSafeToken(draftKey)}-${toDomSafeToken(field)}`
);

const getResponseErrorMessage = async (response, fallbackMessage) => {
  if (!response) {
    return fallbackMessage;
  }
  const jsonPayload = await response.clone().json().catch(() => null);
  if (jsonPayload && typeof jsonPayload.error === 'string' && jsonPayload.error.trim()) {
    return jsonPayload.error.trim();
  }
  if (response.status === 413) {
    return 'Upload too large. Reduce CSV size or increase upload size limits.';
  }
  const textPayload = await response.text().catch(() => '');
  const compact = String(textPayload || '').replace(/\s+/g, ' ').trim();
  if (compact) {
    return `${fallbackMessage} (HTTP ${response.status}): ${compact.slice(0, 180)}`;
  }
  return `${fallbackMessage} (HTTP ${response.status})`;
};

const sleep = (ms) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

const toIsoDateOnly = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const getDefaultTipoffStartDate = () => {
  const base = new Date();
  base.setHours(0, 0, 0, 0);
  base.setDate(base.getDate() - DEFAULT_FILTER_DAYS_BACK);
  return toIsoDateOnly(base);
};

const parseIsoDateBoundaryMillis = (value, { endOfDay = false } = {}) => {
  if (!value) return null;
  const match = String(value).trim().match(ISO_DATE_ONLY);
  if (!match) return null;
  const [, yearRaw, monthRaw, dayRaw] = match;
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  const date = endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day, 0, 0, 0, 0);
  const millis = date.getTime();
  return Number.isNaN(millis) ? null : millis;
};

const formatIsoDateForDisplay = (value) => {
  if (!value) return '';
  const match = String(value).trim().match(ISO_DATE_ONLY);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

const buildDateRangeSummary = (start, end) => {
  const startLabel = formatIsoDateForDisplay(start);
  const endLabel = formatIsoDateForDisplay(end);
  if (startLabel && endLabel) return `${startLabel} to ${endLabel}`;
  if (startLabel) return `${startLabel} onward`;
  if (endLabel) return `Until ${endLabel}`;
  return 'All dates';
};

const buildDefaultGameFilters = () => ({
  tipoffStart: getDefaultTipoffStartDate(),
  tipoffEnd: '',
  competition: '',
  home: '',
  away: '',
  team: '',
  status: ''
});

const createEmptyValues = () => ({
  competition: '',
  status: 'UPCOMING',
  tipoff: '',
  home_name: '',
  home_score: '',
  away_name: '',
  away_score: '',
  link_url: '',
  watch_url: '',
  ticket_url: '',
  teaser: '',
  notes: ''
});

const EMPTY_KEY_FORM = {
  label: '',
  domain: '',
  allowed_origins: '',
  allowed_competitions: '',
  status: 'active'
};

const getOriginFromUrl = (value) => {
  if (!value) return '';
  try {
    return new URL(value).origin;
  } catch (err) {
    return '';
  }
};

const buildDefaultAllowedOrigins = () => {
  const origins = new Set();
  const mainSiteOrigin = getOriginFromUrl(MAIN_SITE_EMBED_URL);
  if (mainSiteOrigin) {
    origins.add(mainSiteOrigin);
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    origins.add(window.location.origin);
  }
  return Array.from(origins).join(',');
};

const buildKeyForm = (allowedOrigins) => ({
  ...EMPTY_KEY_FORM,
  allowed_origins: allowedOrigins || ''
});

const ensureTrailingSlash = (value) => (value.endsWith('/') ? value : `${value}/`);

const resolveAbsoluteApiBase = () => {
  const base = API_BASE || '/api';
  if (typeof window === 'undefined') {
    return base;
  }
  if (/^https?:\/\//i.test(base)) {
    return base;
  }
  const normalized = base.startsWith('/') ? base : `/${base}`;
  return `${window.location.origin}${normalized}`;
};

const buildBannerFeedUrl = () => {
  const base = resolveAbsoluteApiBase();
  try {
    return new URL('games/banner', ensureTrailingSlash(base)).toString();
  } catch (err) {
    return `${String(base).replace(/\/$/, '')}/games/banner`;
  }
};

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const fromUkDateTime = (value) => {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  const match = trimmed.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!match) {
    return trimmed;
  }
  const [, dd, mm, yyyy, hh, min, ss = '00'] = match;
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T${hh.padStart(2, '0')}:${min}:${ss}`;
};

const toIsoString = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const parseScore = (value) => {
  if (value === '' || value === null || typeof value === 'undefined') {
    return null;
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return null;
  }
  return numeric;
};

const toUnixMillis = (value) => {
  if (!value) return null;
  const normalized = fromUkDateTime(value);
  const date = new Date(normalized || value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.getTime();
};

const buildGamePayload = (values) => ({
  competition: values.competition,
  status: values.status,
  priority: 0,
  tipoff: toIsoString(fromUkDateTime(values.tipoff)),
  home_name: values.home_name,
  home_score: parseScore(values.home_score),
  away_name: values.away_name,
  away_score: parseScore(values.away_score),
  link_url: values.link_url,
  watch_url: values.watch_url,
  ticket_url: values.ticket_url,
  teaser: values.teaser,
  notes: values.notes
});

const mapGameToDraft = (game) => ({
  key: String(game.id),
  isNew: false,
  record: game,
  values: {
    competition: game.competition || game.comp || '',
    status: game.status || 'UPCOMING',
    tipoff: toDateTimeLocal(game.tipoff),
    home_name: game.home_name || game.home?.name || '',
    home_score: game.home?.score ?? '',
    away_name: game.away_name || game.away?.name || '',
    away_score: game.away?.score ?? '',
    link_url: game.link_url || game.link || '',
    watch_url: game.watch_url || game.watch_game || '',
    ticket_url: game.ticket_url || '',
    teaser: game.teaser || '',
    notes: game.notes || ''
  },
  saving: false,
  error: ''
});

const createEmptyDraft = () => ({
  key: `new-${Date.now()}`,
  isNew: true,
  record: null,
  values: createEmptyValues(),
  saving: false,
  error: ''
});

const mergeGamesById = (existingGames, incomingGames) => {
  if (!Array.isArray(existingGames)) return [];
  if (!Array.isArray(incomingGames) || incomingGames.length === 0) {
    return existingGames;
  }
  const updatesById = new Map();
  incomingGames.forEach((game) => {
    const gameId = game?.id;
    if (gameId === null || typeof gameId === 'undefined') return;
    updatesById.set(String(gameId), game);
  });
  if (updatesById.size === 0) {
    return existingGames;
  }
  const applied = new Set();
  const next = existingGames.map((record) => {
    const recordId = record?.id;
    const key = recordId === null || typeof recordId === 'undefined' ? '' : String(recordId);
    if (updatesById.has(key)) {
      applied.add(key);
      return updatesById.get(key);
    }
    return record;
  });
  updatesById.forEach((record, key) => {
    if (!applied.has(key)) {
      next.push(record);
    }
  });
  return next;
};

const EMPTY_GAME_VALUES = createEmptyValues();
const GAME_VALUE_FIELDS = Object.keys(EMPTY_GAME_VALUES);

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const TEAM_PREFIXES = ['b braun'];

const normalizeTeamName = (value) => {
  let normalized = normalizeText(value)
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  TEAM_PREFIXES.forEach((prefix) => {
    if (normalized.startsWith(`${prefix} `)) {
      normalized = normalized.slice(prefix.length).trim();
    }
  });
  return normalized;
};

const normalizeCompetition = (value) => {
  let normalized = normalizeText(value)
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (normalized.startsWith('slb ')) {
    normalized = normalized.slice(4).trim();
  }
  return normalized;
};

const normalizeDateKey = (value) => {
  if (!value) return '';
  const trimmed = String(value).trim();
  let match = trimmed.match(UK_DATE_ONLY);
  if (match) {
    const [, dd, mm, yyyy] = match;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  const normalized = fromUkDateTime(trimmed);
  const date = new Date(normalized || trimmed);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const buildMatchKey = (record) => {
  const dateKey = normalizeDateKey(record.tipoff);
  const compKey = normalizeCompetition(record.competition);
  const homeKey = normalizeTeamName(record.home_name);
  const awayKey = normalizeTeamName(record.away_name);
  return `${dateKey}|${compKey}|${homeKey}|${awayKey}`;
};

const UK_DATE_TIME = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/;
const UK_DATE_ONLY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

const splitTipoffInput = (value) => {
  if (!value) return { date: '', time: '' };
  const trimmed = String(value).trim();
  let match = trimmed.match(UK_DATE_TIME);
  if (match) {
    const [, dd, mm, yyyy, hh, min] = match;
    return { date: `${dd}/${mm}/${yyyy}`, time: `${hh}:${min}` };
  }
  match = trimmed.match(UK_DATE_ONLY);
  if (match) {
    const [, dd, mm, yyyy] = match;
    return { date: `${dd}/${mm}/${yyyy}`, time: '' };
  }
  const normalized = fromUkDateTime(trimmed);
  const date = new Date(normalized || trimmed);
  if (Number.isNaN(date.getTime())) return { date: '', time: '' };
  const local = toDateTimeLocal(date.toISOString());
  if (!local) return { date: '', time: '' };
  const [datePart, timePart] = local.split(' ');
  return { date: datePart || '', time: timePart || '' };
};

const buildTipoffFromParts = (datePart, timePart) => {
  const date = String(datePart || '').trim();
  const time = String(timePart || '').trim();
  if (date && time) return `${date} ${time}`;
  if (date) return date;
  if (time) return time;
  return '';
};

const normalizeComparableValue = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === null || typeof value === 'undefined') {
    return '';
  }
  return String(value);
};

const areGameValuesEqual = (a, b) => {
  return GAME_VALUE_FIELDS.every((field) => normalizeComparableValue(a[field]) === normalizeComparableValue(b[field]));
};

const getOriginalValuesForDraft = (draft) => {
  if (draft.isNew || !draft.record) {
    return EMPTY_GAME_VALUES;
  }
  return mapGameToDraft(draft.record).values;
};

const draftHasUnsavedChanges = (draft) => {
  if (draft.isNew) return true;
  return !areGameValuesEqual(draft.values, getOriginalValuesForDraft(draft));
};

const parseDelimitedRows = (text, delimiter) => {
  const rows = [];
  let row = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];
    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && char === delimiter) {
      row.push(current);
      current = '';
      continue;
    }
    if (!inQuotes && (char === '\n' || char === '\r')) {
      if (char === '\r' && next === '\n') {
        i += 1;
      }
      row.push(current);
      if (row.length > 1 || row[0] !== '') {
        rows.push(row);
      }
      row = [];
      current = '';
      continue;
    }
    current += char;
  }
  row.push(current);
  if (row.length > 1 || row[0] !== '') {
    rows.push(row);
  }
  return rows;
};

const escapeCsvValue = (value) => {
  if (value === undefined || value === null) {
    return '';
  }
  const stringValue = String(value);
  if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('\r')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const buildLogoMapCsv = (rows) => {
  const headerLine = ['team_name', 'logo_url'].join(',');
  const dataLines = (rows || []).map((row) => {
    const values = [row?.name || '', row?.url || ''];
    return values.map(escapeCsvValue).join(',');
  });
  return [headerLine, ...dataLines].join('\r\n');
};

const parseLogoMapText = (text) => {
  const trimmed = String(text || '').trim();
  if (!trimmed) return [];
  const firstLine = trimmed.split(/\r?\n/)[0] || '';
  const delimiter = firstLine.includes('\t') ? '\t' : ',';
  const rows = parseDelimitedRows(trimmed, delimiter);
  if (rows.length === 0) return [];
  const header = rows[0].map((cell) => normalizeText(cell).replace(/\s+/g, '_'));
  const nameHeaders = ['team', 'team_name', 'name'];
  const urlHeaders = ['logo', 'logo_url', 'url'];
  const hasHeader = header.some((value) => nameHeaders.includes(value))
    && header.some((value) => urlHeaders.includes(value));
  const nameIndex = hasHeader ? header.findIndex((value) => nameHeaders.includes(value)) : 0;
  const urlIndex = hasHeader ? header.findIndex((value) => urlHeaders.includes(value)) : 1;
  const dataRows = hasHeader ? rows.slice(1) : rows;
  if (nameIndex < 0 || urlIndex < 0) {
    throw new Error('Mapping CSV must include team and logo columns.');
  }
  const deduped = new Map();
  dataRows.forEach((row) => {
    const rawName = row[nameIndex] ?? '';
    const rawUrl = row[urlIndex] ?? '';
    const name = String(rawName || '').trim();
    const url = String(rawUrl || '').trim();
    const key = normalizeTeamName(name);
    if (!key || !url) return;
    deduped.set(key, { name, url });
  });
  return Array.from(deduped.values());
};

const parseExternalListInput = (input) => {
  const trimmed = String(input || '').trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.games)) return parsed.games;
    return [];
  }
  const firstLine = trimmed.split(/\r?\n/)[0] || '';
  const delimiter = firstLine.includes('\t') ? '\t' : ',';
  const rows = parseDelimitedRows(trimmed, delimiter);
  if (rows.length === 0) return [];
  const header = rows[0].map((cell) => normalizeText(cell));
  return rows.slice(1).map((row) => {
    const record = {};
    header.forEach((key, idx) => {
      if (!key) return;
      record[key] = row[idx] ?? '';
    });
    return record;
  });
};

const mapExternalRecord = (record) => {
  const getValue = (...keys) => {
    for (const key of keys) {
      if (record && Object.prototype.hasOwnProperty.call(record, key)) {
        const value = record[key];
        if (value !== null && typeof value !== 'undefined' && String(value).trim() !== '') {
          return String(value).trim();
        }
      }
    }
    return '';
  };
  const date = getValue('tipoff_date', 'date_event', 'dateevent', 'date');
  const time = getValue('tipoff_time', 'time', 'start_time');
  let tipoff = '';
  if (date) {
    tipoff = time ? `${date} ${time}` : date;
  } else {
    tipoff = getValue('tipoff', 'tipoff_iso', 'tipoff_at');
    if (tipoff && time && tipoff === date) {
      tipoff = `${tipoff} ${time}`;
    }
  }
  return {
    competition: getValue('competition', 'comp', 'league'),
    tipoff,
    home_name: getValue('home_name', 'home team', 'home', 'home_team'),
    away_name: getValue('away_name', 'away team', 'away', 'away_team')
  };
};

const formatMatchLabel = (record) => {
  const dateKey = normalizeDateKey(record.tipoff) || 'Unknown date';
  const comp = record.competition || 'Unknown competition';
  const home = record.home_name || 'Unknown home';
  const away = record.away_name || 'Unknown away';
  return `${dateKey} · ${comp} · ${home} vs ${away}`;
};

const parseScorePair = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return { home: '', away: '' };
  const match = raw.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (!match) return { home: '', away: '' };
  return { home: match[1], away: match[2] };
};

const normalizeExternalStatus = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  if (raw.includes('final')) return 'FINAL';
  if (raw.includes('live')) return 'LIVE';
  if (raw.includes('postponed')) return 'POSTPONED';
  if (raw.includes('cancel')) return 'CANCELLED';
  if (raw.includes('upcoming') || raw.includes('scheduled')) return 'UPCOMING';
  return raw.toUpperCase();
};

const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const sanitizeHexColor = (value, fallback) => {
  const candidate = String(value || '').trim();
  return HEX_COLOR_PATTERN.test(candidate) ? candidate : fallback;
};

const sanitizeFontFamily = (value, fallback) => {
  const candidate = String(value || '').trim();
  if (!candidate) return fallback;
  // Prevent CSS/script injection through the font family field.
  if (/[<>{};]/.test(candidate)) return fallback;
  return candidate;
};

export const DEFAULT_WIDGET_THEME = {
  bg: '#0d1b2b',
  card: '#14263a',
  line: '#294566',
  text: '#eaf1f8',
  muted: '#c8d4e5',
  accent: '#dcd2ff',
  accentDark: '#bfb2ff',
  cardHeaderBg: '#102336',
  cardHeaderText: '#d3e0f2',
  cardHeaderStatus: '#dcd2ff',
  headbarBg: '#12253a',
  headbarBorder: '#1f3855',
  controlsBg: '#0f2033',
  controlsLabel: '#d3e0f2',
  controlsText: '#eaf1f8',
  scrollThumb: '#1e3a5a',
  fontBase: 'system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
  fontTitle: 'inherit',
  fontTeam: 'inherit'
};

const resolveWidgetTheme = (themeOverrides = {}) => {
  const merged = { ...DEFAULT_WIDGET_THEME, ...(themeOverrides || {}) };
  return {
    bg: sanitizeHexColor(merged.bg, DEFAULT_WIDGET_THEME.bg),
    card: sanitizeHexColor(merged.card, DEFAULT_WIDGET_THEME.card),
    line: sanitizeHexColor(merged.line, DEFAULT_WIDGET_THEME.line),
    text: sanitizeHexColor(merged.text, DEFAULT_WIDGET_THEME.text),
    muted: sanitizeHexColor(merged.muted, DEFAULT_WIDGET_THEME.muted),
    accent: sanitizeHexColor(merged.accent, DEFAULT_WIDGET_THEME.accent),
    accentDark: sanitizeHexColor(merged.accentDark, DEFAULT_WIDGET_THEME.accentDark),
    cardHeaderBg: sanitizeHexColor(merged.cardHeaderBg, DEFAULT_WIDGET_THEME.cardHeaderBg),
    cardHeaderText: sanitizeHexColor(merged.cardHeaderText, DEFAULT_WIDGET_THEME.cardHeaderText),
    cardHeaderStatus: sanitizeHexColor(merged.cardHeaderStatus, DEFAULT_WIDGET_THEME.cardHeaderStatus),
    headbarBg: sanitizeHexColor(merged.headbarBg, DEFAULT_WIDGET_THEME.headbarBg),
    headbarBorder: sanitizeHexColor(merged.headbarBorder, DEFAULT_WIDGET_THEME.headbarBorder),
    controlsBg: sanitizeHexColor(merged.controlsBg, DEFAULT_WIDGET_THEME.controlsBg),
    controlsLabel: sanitizeHexColor(merged.controlsLabel, DEFAULT_WIDGET_THEME.controlsLabel),
    controlsText: sanitizeHexColor(merged.controlsText, DEFAULT_WIDGET_THEME.controlsText),
    scrollThumb: sanitizeHexColor(merged.scrollThumb, DEFAULT_WIDGET_THEME.scrollThumb),
    fontBase: sanitizeFontFamily(merged.fontBase, DEFAULT_WIDGET_THEME.fontBase),
    fontTitle: sanitizeFontFamily(merged.fontTitle, DEFAULT_WIDGET_THEME.fontTitle),
    fontTeam: sanitizeFontFamily(merged.fontTeam, DEFAULT_WIDGET_THEME.fontTeam)
  };
};

const serializeForInlineScript = (value) => {
  return JSON.stringify(value)
    .replace(/<\//g, '<\\/')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
};

export const WIDGET_SNIPPET = (apiKey, feedUrl, themeOverrides = {}, options = {}) => {
  const safeFeedUrl = String(feedUrl || '').replace(/'/g, "\\'");
  const resolvedTheme = resolveWidgetTheme(themeOverrides);
  const preloadedGames = Array.isArray(options?.preloadedGames) ? options.preloadedGames : null;
  const preloadedGamesJson = serializeForInlineScript(preloadedGames);
  const preloadedGamesOnly = Boolean(options?.preloadedGamesOnly && preloadedGames);
  return `
<!-- ===== AllBasketball Games Banner ===== -->
<div data-slb-widget-host style="display:block;width:100%;margin:0;padding:0;border:0;background:transparent"></div>

<script>
(function initWidget() {
  function init() {
  const FEED_URL = '${safeFeedUrl}';
  const BANNER_KEY = '@@API_KEY@@';
  const LIVE_GAME_REFRESH_MS = 25000;
  const IDLE_REFRESH_MS = 90000;
  const INITIAL_PAST_COUNT = 10;
  const INITIAL_FUTURE_COUNT = 10;
  const LOAD_MORE_STEP = 10;
  const ATTRIBUTION_OWNER_DOMAIN = 'allbasketball.co.uk';
  const EXTERNAL_FEED_UPDATE_EVENT = 'SLB_WIDGET_FEED_UPDATE';
  const PRELOADED_GAMES = @@PRELOADED_GAMES@@;
  const PREVIEW_USE_STATIC_FEED = @@PREVIEW_USE_STATIC_FEED@@;

  const scriptTag = document.currentScript;
  const siblingHost = scriptTag && scriptTag.previousElementSibling;
  const fallbackHost = document.querySelector('[data-slb-widget-host]:not([data-slb-widget-mounted])');
  const host = siblingHost && siblingHost.hasAttribute('data-slb-widget-host')
    ? siblingHost
    : fallbackHost;

  if (!host) {
    setTimeout(init, 50);
    return;
  }
  if (host.getAttribute('data-slb-widget-mounted') === '1') {
    return;
  }
  host.setAttribute('data-slb-widget-mounted', '1');

  let root = host.shadowRoot || null;
  if (!root && typeof host.attachShadow === 'function') {
    try {
      root = host.attachShadow({ mode: 'open' });
    } catch (err) {
      root = null;
    }
  }
  if (!root) {
    root = host;
  }

  const widgetMarkup = \`
  <div class="slb-wrap" id="slbBanner" role="region" aria-label="Basketball Live Scores">
    <div class="slb-headbar">
      <div class="slb-title-wrap">
        <div class="slb-title">Basketball — Fixtures &amp; Results</div>
        <a class="slb-powered" href="https://allbasketball.co.uk" target="_blank" rel="noopener noreferrer" aria-label="Powered by allbasketball.co.uk">Powered by allbasketball.co.uk</a>
      </div>
      <div class="slb-controls">
        <label for="slbComp" class="slb-filter-label">Competition:</label>
        <select id="slbComp" class="slb-select" aria-label="Filter by competition">
          <option value="">All competitions</option>
        </select>
        <div class="slb-ctrl">
          <button class="slb-btn" data-action="refresh" aria-label="Refresh scores">↻</button>
          <button class="slb-btn" data-dir="-1" aria-label="Scroll left">◀</button>
          <button class="slb-btn" data-dir="1" aria-label="Scroll right">▶</button>
        </div>
      </div>
    </div>
    <div class="slb-inner" id="slbInner" aria-live="polite"></div>
  </div>
  \`;

  const baseStyles = \`
  :host{all:initial;display:block;width:100%;margin:0;padding:0;border:0;background:transparent}
  :host,:host *,.slb-wrap,.slb-wrap *{box-sizing:border-box}
  .slb-wrap{
    --bg:#0d1b2b; --card:#14263a; --line:#294566;
    --text:#eaf1f8; --muted:#c8d4e5;
    --accent:#dcd2ff; --accentDark:#bfb2ff;
    --cardHeaderBg:#102336; --cardHeaderText:#d3e0f2; --cardHeaderStatus:#dcd2ff;
    font-family:system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;background:var(--bg);color:var(--text);
    border-radius:10px;overflow:hidden;box-shadow:0 3px 10px rgba(0,0,0,.3);line-height:1.25
  }
  .slb-headbar{display:flex;justify-content:space-between;align-items:center;gap:8px;padding:8px 12px;background:#12253a;border-bottom:1px solid #1f3855;flex-wrap:wrap}
  .slb-title-wrap{display:flex;flex-direction:column;gap:2px}
  .slb-title{font-size:14px;font-weight:700;letter-spacing:.35px;text-transform:uppercase;color:var(--accent)}
  .slb-powered{font-size:11px;color:var(--muted);text-decoration:none;line-height:1.2}
  .slb-powered:hover{color:var(--accent);text-decoration:underline}
  .slb-controls{display:flex;align-items:center;gap:8px;margin-left:auto;flex-wrap:wrap}
  .slb-filter-label{font-size:12px;color:#d3e0f2}
  .slb-select{
    background:#0f2033;color:#eaf1f8;border:1px solid #294566;border-radius:7px;
    padding:4px 28px 4px 8px;font-size:12px;min-width:160px;appearance:none;
    background-image:linear-gradient(45deg,transparent 50%,var(--accent) 50%),
                     linear-gradient(135deg,var(--accent) 50%,transparent 50%);
    background-position:right 10px center,right 6px center;
    background-size:6px 6px,6px 6px;background-repeat:no-repeat;
  }
  .slb-ctrl .slb-btn{
    background:var(--accent);color:#0d1b2b;border:none;border-radius:7px;
    width:30px;height:24px;padding:0;display:inline-flex;align-items:center;justify-content:center;
    cursor:pointer;font-size:12px;font-weight:800;line-height:1;transition:transform .15s ease,background .15s ease
  }
  .slb-ctrl .slb-btn:hover{background:var(--accentDark)}
  .slb-inner{display:flex;gap:12px;padding:10px 12px;overflow-x:auto;scroll-behavior:smooth}
  .slb-inner::-webkit-scrollbar{height:8px}
  .slb-inner::-webkit-scrollbar-thumb{background:#1e3a5a;border-radius:8px}
  .slb-card{display:grid;grid-template-rows:34px 44px 44px 40px;width:260px;min-width:260px;max-width:260px;flex:0 0 260px;background:var(--card);border:1px solid var(--line);border-radius:10px;overflow:hidden;transition:border-color .18s ease,transform .18s ease}
  .slb-card:hover{border-color:#3b5f8a;transform:translateY(-2px)}
  .slb-card.live{border-color:var(--accent)}
  .slb-more{min-width:220px;min-height:162px;display:flex;align-items:center;justify-content:center;border:1px dashed var(--line);border-radius:10px;background:#102336;color:var(--accent);font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.4px;cursor:pointer;transition:transform .18s ease,border-color .18s ease,color .18s ease}
  .slb-more:hover{border-color:var(--accent);color:var(--accentDark);transform:translateY(-2px)}
  .slb-card .top{display:flex;justify-content:space-between;align-items:center;padding:0 10px;border-bottom:1px solid var(--line);background:var(--cardHeaderBg);font-size:10.5px;text-transform:uppercase}
  .slb-card .comp{color:var(--cardHeaderText);font-weight:600}
  .slb-card .status{color:var(--cardHeaderStatus);font-weight:900;display:flex;align-items:center;gap:6px}
  .slb-card .status .slb-teaser{font-weight:800;opacity:.95}
  .slb-card.live .status .dot{width:7px;height:7px;border-radius:50%;background:var(--cardHeaderStatus);display:inline-block;animation:slbPulse 1s infinite ease-in-out}
  @keyframes slbPulse{0%,100%{opacity:.35}50%{opacity:1}}
  .slb-row{display:flex;align-items:center;gap:10px;padding:0 10px}
  .slb-team{display:flex;align-items:center;gap:10px;font-size:16px;font-weight:800;color:var(--text);line-height:1.1}
  .slb-team img{width:28px;height:28px;object-fit:contain;filter:drop-shadow(0 0 2px rgba(0,0,0,.5))}
  .slb-score{margin-left:auto;font-size:22px;font-weight:900;color:var(--accent)}
  .slb-foot{display:flex;align-items:center;justify-content:space-between;padding:0 10px;border-top:1px solid var(--line);font-size:12px;color:var(--muted)}
  .slb-actions{display:flex;gap:6px;align-items:center}
  .slb-action{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:6px;border:1px solid transparent;color:var(--accent);text-decoration:none;transition:color .15s ease,border-color .15s ease,background .15s ease,transform .15s ease}
  .slb-action:hover{color:var(--accentDark);border-color:var(--accentDark);background:rgba(220,210,255,.12);transform:translateY(-1px)}
  .slb-action svg{width:20px;height:20px;stroke:currentColor;fill:none;stroke-width:1.7;stroke-linecap:round;stroke-linejoin:round}
  .slb-action svg .slb-icon-fill{fill:currentColor;stroke:none}
  @media(max-width:600px){
    .slb-headbar{padding:6px 10px}
    .slb-title{font-size:12px}
    .slb-powered{font-size:10px}
    .slb-select{min-width:140px;font-size:11px;padding:3px 24px 3px 6px}
    .slb-ctrl .slb-btn{width:26px;height:20px;font-size:11px}
    .slb-inner{gap:8px;padding:8px 10px}
    .slb-card{width:220px;min-width:220px;max-width:220px;flex-basis:220px;grid-template-rows:28px 38px 38px 34px}
    .slb-card .top{font-size:9.5px;padding:0 8px}
    .slb-row{gap:8px;padding:0 8px}
    .slb-team{font-size:14px;gap:8px}
    .slb-more{min-width:180px;min-height:138px;font-size:11px}
    .slb-action{width:26px;height:26px}
    .slb-action svg{width:18px;height:18px}
  }
  \`;

  const themeStyles = \`
  .slb-wrap{
    --bg:@@THEME_BG@@;
    --card:@@THEME_CARD@@;
    --line:@@THEME_LINE@@;
    --text:@@THEME_TEXT@@;
    --muted:@@THEME_MUTED@@;
    --accent:@@THEME_ACCENT@@;
    --accentDark:@@THEME_ACCENT_DARK@@;
    --cardHeaderBg:@@THEME_CARD_HEADER_BG@@;
    --cardHeaderText:@@THEME_CARD_HEADER_TEXT@@;
    --cardHeaderStatus:@@THEME_CARD_HEADER_STATUS@@;
    font-family:@@THEME_FONT_BASE@@;
  }
  .slb-title{font-family:@@THEME_FONT_TITLE@@}
  .slb-team{font-family:@@THEME_FONT_TEAM@@}
  .slb-headbar{background:@@THEME_HEADBAR_BG@@;border-bottom-color:@@THEME_HEADBAR_BORDER@@}
  .slb-filter-label{color:@@THEME_CONTROLS_LABEL@@}
  .slb-select{background:@@THEME_CONTROLS_BG@@}
  .slb-select{color:@@THEME_CONTROLS_TEXT@@}
  .slb-inner::-webkit-scrollbar-thumb{background:@@THEME_SCROLL_THUMB@@}
  \`;

  root.innerHTML = '<style>' + baseStyles + '</style><style>' + themeStyles + '</style>' + widgetMarkup;

  const elInner = root.querySelector('#slbInner');
  const elSelect = root.querySelector('#slbComp');
  const elPoweredBy = root.querySelector('.slb-powered');

  let pastLimit = INITIAL_PAST_COUNT;
  let futureLimit = INITIAL_FUTURE_COUNT;
  let preloadedFeedConsumed = false;
  let hasExternalFeedUpdate = false;
  let refreshTimerId = null;
  let refreshInFlight = false;

  // Safety check - if DOM elements aren't ready, retry after a short delay
  if (!elInner || !elSelect) {
    setTimeout(init, 50);
    return;
  }

  const hostname = String(window.location.hostname || '').toLowerCase();
  const isOwnerHost = hostname === ATTRIBUTION_OWNER_DOMAIN
    || hostname.endsWith('.' + ATTRIBUTION_OWNER_DOMAIN);
  if (isOwnerHost && elPoweredBy) {
    elPoweredBy.style.display = 'none';
  }

  const buildNoCacheFeedUrl = () => {
    try {
      const parsed = new URL(FEED_URL, window.location.href);
      parsed.searchParams.set('_', String(Date.now()));
      return parsed.toString();
    } catch (err) {
      const separator = FEED_URL.includes('?') ? '&' : '?';
      return \`\${FEED_URL}\${separator}_=\${Date.now()}\`;
    }
  };

  const fetchFeed = async () => {
    if (Array.isArray(PRELOADED_GAMES)) {
      if (PREVIEW_USE_STATIC_FEED) {
        if (hasExternalFeedUpdate) {
          return Array.isArray(RAW) ? RAW : [];
        }
        preloadedFeedConsumed = true;
        return PRELOADED_GAMES;
      }
      if (!preloadedFeedConsumed) {
        preloadedFeedConsumed = true;
        return PRELOADED_GAMES;
      }
    }
    const response = await fetch(buildNoCacheFeedUrl(), {
      headers: { 'X-Banner-Key': BANNER_KEY },
      cache: 'no-store'
    });
    if (response.status === 304) {
      return Array.isArray(RAW) ? RAW : [];
    }
    if (!response.ok) throw new Error('Failed to load banner feed');
    return response.json();
  };

  const pad2 = (value) => String(value).padStart(2, '0');
  const fmtDate = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return pad2(parsed.getDate()) + '/' + pad2(parsed.getMonth() + 1) + '/' + parsed.getFullYear() + ' ' + pad2(parsed.getHours()) + ':' + pad2(parsed.getMinutes());
  };

  const parseDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const getTipoffMillis = (game) => {
    const raw = game?.tipoff_ts;
    if (raw !== null && typeof raw !== 'undefined') {
      const numeric = Number(raw);
      if (Number.isFinite(numeric) && numeric > 0) return numeric;
    }
    const parsed = parseDate(game?.tipoff);
    return parsed ? parsed.getTime() : null;
  };

  const isLive = (game) => (game?.status || '').toUpperCase() === 'LIVE';
  const makeKey = (g) => {
    if (g && g.id != null && g.id !== '') return \`id:\${g.id}\`;
    return \`\${g.comp || ''}::\${g.home?.name || g.home}::\${g.away?.name || g.away}\`;
  };
  const latestVersionByKey = new Map();
  const parseGameVersion = (game) => {
    const raw = String(game?.updated_at || '').trim();
    if (!raw) return null;
    const normalized = /^\\d{4}-\\d{2}-\\d{2} /.test(raw)
      ? raw.replace(' ', 'T') + 'Z'
      : raw;
    const stamp = Date.parse(normalized);
    return Number.isFinite(stamp) ? stamp : null;
  };

  const scoreText = (value) => {
    if (value == null || value === '') return '—';
    return Number(value);
  };

  const statusText = (g) => {
    const status = (g.status || '').toUpperCase();
    if (status === 'LIVE') {
      const detail = (g.status_detail || '').trim();
      return detail || 'LIVE';
    }
    if (status === 'FINAL') return 'FINAL';
    if (status === 'POSTPONED') return 'Postponed';
    return fmtDate(g.tipoff) || 'TBD';
  };

  const renderStatusHTML = (g) => {
    const teaser = (g.teaser || '').trim();
    const teaserDisplay = teaser
      ? (teaser.startsWith('(') && teaser.endsWith(')') ? teaser : \`(\${teaser})\`)
      : '';
    const teaserHTML = teaserDisplay ? \`<span class="slb-teaser">\${teaserDisplay}</span>\` : '';
    return \`\${statusText(g)}\${isLive(g) ? '<span class="dot" aria-hidden="true"></span>' : ''}\${teaserHTML}\`;
  };

  const cardHTML = (g) => {
    const home = g.home || { name: g.home_name, logo: '', score: g.home_score };
    const away = g.away || { name: g.away_name, logo: '', score: g.away_score };
    const tipoff = fmtDate(g.tipoff) || 'TBD';
    const status = renderStatusHTML(g);
    const liveClass = isLive(g) ? ' live' : '';
    const logoProxy = FEED_URL.replace(/\\/games\\/banner\\/?$/i, '/games/logo');
    const makeLogo = (team) => {
      if (!team.logo) return '';
      const src = \`\${logoProxy}?url=\${encodeURIComponent(team.logo)}\`;
      return \`<img src="\${src}" alt="\${team.name || ''}" referrerpolicy="no-referrer" loading="lazy" decoding="async">\`;
    };

    return \`
      <article class="slb-card\${liveClass}" data-key="\${makeKey(g)}">
        <div class="top">
          <span class="comp">\${g.comp || g.competition || 'SLB'}</span>
          <span class="status" data-status="\${g.status || ''}">
            \${status}
          </span>
        </div>
        <div class="slb-row">
          <div class="slb-team">\${makeLogo(home)}\${home.name || ''}</div>
          <div class="slb-score home-score">\${scoreText(home.score)}</div>
        </div>
        <div class="slb-row">
          <div class="slb-team">\${makeLogo(away)}\${away.name || ''}</div>
          <div class="slb-score away-score">\${scoreText(away.score)}</div>
        </div>
        <div class="slb-foot">
          <span class="slb-when">\${tipoff}</span>
          <div class="slb-actions">
            \${g.ticket_url ? \`<a class="slb-action slb-action--ticket" href="\${g.ticket_url}" target="_blank" rel="noopener" title="Buy tickets" aria-label="Buy tickets">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M4 7h16v3a2 2 0 0 0 0 4v3H4v-3a2 2 0 0 0 0-4V7z"></path>
                <path d="M9 7v10"></path>
              </svg>
            </a>\` : ''}
            \${g.link ? \`<a class="slb-action slb-action--highlights" href="\${g.link}" target="_blank" rel="noopener" title="Watch highlights" aria-label="Watch highlights">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <rect x="3" y="6" width="18" height="12" rx="3" ry="3"></rect>
                <polygon class="slb-icon-fill" points="10,9 16,12 10,15"></polygon>
              </svg>
            </a>\` : ''}
            \${g.watch_game ? \`<a class="slb-action slb-action--watch" href="\${g.watch_game}" target="_blank" rel="noopener" title="Watch full game" aria-label="Watch full game">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <rect x="3" y="6" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M8 21h8"></path>
                <path d="M12 17v4"></path>
                <path d="M9 6L6 3"></path>
                <path d="M15 6l3-3"></path>
              </svg>
            </a>\` : ''}
          </div>
        </div>
      </article>
    \`;
  };

  const sortGames = (games) => {
    return games
      .map((g) => ({ ...g, tipoffTime: getTipoffMillis(g) ?? Infinity }))
      .sort((a, b) => {
        if (isLive(a) && !isLive(b)) return -1;
        if (!isLive(a) && isLive(b)) return 1;
        if (a.tipoffTime !== b.tipoffTime) return a.tipoffTime - b.tipoffTime;
        return (b.priority || 0) - (a.priority || 0);
      });
  };

  const populateFilter = (games) => {
    const seen = new Set();
    elSelect.innerHTML = '<option value="">All competitions</option>';
    sortGames(games).forEach((g) => {
      const comp = (g.comp || g.competition || '').trim();
      if (!comp || seen.has(comp)) return;
      seen.add(comp);
      const opt = document.createElement('option');
      opt.value = comp;
      opt.textContent = comp;
      elSelect.appendChild(opt);
    });
  };

  const filterGames = (games) => {
    const comp = elSelect.value;
    if (!comp) return games;
    return games.filter((g) => (g.comp || g.competition || '') === comp);
  };

  const splitGamesByTime = (games) => {
    const now = Date.now();
    const past = [];
    const future = [];
    games.forEach((game) => {
      const tipoffTime = getTipoffMillis(game);
      const live = isLive(game);
      const entry = { ...game, tipoffTime: tipoffTime ?? Infinity, live };
      if (live) {
        future.push(entry);
        return;
      }
      if (Number.isFinite(entry.tipoffTime) && entry.tipoffTime < now) {
        past.push(entry);
      } else {
        future.push(entry);
      }
    });
    past.sort((a, b) => {
      if (a.tipoffTime !== b.tipoffTime) return b.tipoffTime - a.tipoffTime;
      return (b.priority || 0) - (a.priority || 0);
    });
    future.sort((a, b) => {
      if (a.live && !b.live) return -1;
      if (!a.live && b.live) return 1;
      if (a.tipoffTime !== b.tipoffTime) return a.tipoffTime - b.tipoffTime;
      return (b.priority || 0) - (a.priority || 0);
    });
    return { past, future };
  };

  const buildDisplayState = (games) => {
    const { past, future } = splitGamesByTime(games);
    const pastSlice = past.slice(0, pastLimit);
    const futureSlice = future.slice(0, futureLimit);
    const pastDisplay = [...pastSlice].sort((a, b) => {
      if (a.tipoffTime !== b.tipoffTime) return a.tipoffTime - b.tipoffTime;
      return (b.priority || 0) - (a.priority || 0);
    });
    return {
      list: [...pastDisplay, ...futureSlice],
      pastRemaining: Math.max(0, past.length - pastSlice.length),
      futureRemaining: Math.max(0, future.length - futureSlice.length)
    };
  };

  const moreCardHTML = (direction) => \`
    <button class="slb-more" type="button" data-more="\${direction}" aria-label="Show more \${direction} games">
      Show More
    </button>
  \`;

  const autoCenterFromList = (list) => {
    if (!list.length) return;
    const now = Date.now();
    let bestIndex = -1;
    let bestDiff = Infinity;
    list.forEach((g, i) => {
      const live = g.live ?? isLive(g);
      const t = Number.isFinite(g.tipoffTime) ? g.tipoffTime : getTipoffMillis(g);
      if (live && bestDiff > 0) {
        bestDiff = -1;
        bestIndex = i;
        return;
      }
      if (!Number.isFinite(t)) return;
      const diff = t - now;
      if (diff >= 0 && diff < bestDiff) {
        bestDiff = diff;
        bestIndex = i;
      }
    });
    if (bestIndex === -1) bestIndex = list.length - 1;
    const cards = elInner.querySelectorAll('.slb-card');
    const target = cards[bestIndex];
    if (target) {
      const offset = target.offsetLeft - (elInner.clientWidth / 2 - target.clientWidth / 2);
      setTimeout(() => elInner.scrollTo({ left: offset, behavior: 'smooth' }), 300);
    }
  };

  let RAW = [];
  const resetLimits = () => {
    pastLimit = INITIAL_PAST_COUNT;
    futureLimit = INITIAL_FUTURE_COUNT;
  };

  async function render(options = {}) {
    const {
      fullReload = false,
      resetCounts = false,
      autoCenter = false,
      preserveScroll = false,
      scrollTo = ''
    } = options;
    const previousScrollLeft = elInner.scrollLeft;
    try {
      if (fullReload || !RAW.length) {
        RAW = await fetchFeed();
        populateFilter(RAW);
      }
      if (resetCounts) {
        resetLimits();
      }
      const filtered = filterGames(RAW);
      const { list, pastRemaining, futureRemaining } = buildDisplayState(filtered);
      latestVersionByKey.clear();
      list.forEach((game) => {
        const versionStamp = parseGameVersion(game);
        if (versionStamp != null) {
          latestVersionByKey.set(makeKey(game), versionStamp);
        }
      });
      if (!list.length) {
        elInner.innerHTML = '<div class="slb-empty" style="padding:10px;color:#c8d4e5">No games for this competition.</div>';
        return;
      }
      const html = [
        pastRemaining > 0 ? moreCardHTML('past') : '',
        list.map(cardHTML).join(''),
        futureRemaining > 0 ? moreCardHTML('future') : ''
      ].join('');
      elInner.innerHTML = html;
      if (scrollTo === 'past') {
        elInner.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (scrollTo === 'future') {
        elInner.scrollTo({ left: elInner.scrollWidth, behavior: 'smooth' });
      } else if (preserveScroll) {
        elInner.scrollLeft = previousScrollLeft;
      } else if (autoCenter) {
        autoCenterFromList(list);
      }
    } catch (err) {
      console.error(err);
      elInner.innerHTML = '<div class="slb-empty" style="padding:10px;color:#c8d4e5">Couldn\\'t load scores.</div>';
    }
  }

  root.querySelectorAll('.slb-btn[data-dir]').forEach((btn) => {
    btn.addEventListener('click', () => elInner.scrollBy({ left: (btn.dataset.dir === '1' ? 300 : -300), behavior: 'smooth' }));
  });
  elSelect.addEventListener('change', () => render({ resetCounts: true, autoCenter: true }));
  elInner.addEventListener('click', (event) => {
    const target = event.target && typeof event.target.closest === 'function'
      ? event.target.closest('.slb-more')
      : null;
    if (!target) return;
    const direction = target.dataset.more;
    if (direction === 'past') {
      pastLimit += LOAD_MORE_STEP;
      render({ scrollTo: 'past' });
    } else if (direction === 'future') {
      futureLimit += LOAD_MORE_STEP;
      render({ scrollTo: 'future' });
    }
  });

  render({ fullReload: true, resetCounts: true, autoCenter: true });

  const patchCardsFromFeed = (games) => {
    const sorted = sortGames(filterGames(games));
    const gameMap = new Map();
    sorted.forEach((g) => {
      gameMap.set(makeKey(g), g);
    });

    root.querySelectorAll('#slbInner .slb-card').forEach((card) => {
      const key = card.getAttribute('data-key');
      if (!key) return;
      const g = gameMap.get(key);
      const statusEl = card.querySelector('.status');
      if (!g) return;
      const nextVersion = parseGameVersion(g);
      const currentVersion = latestVersionByKey.get(key);
      if (currentVersion != null && nextVersion != null && nextVersion < currentVersion) {
        return;
      }
      if (nextVersion != null) {
        latestVersionByKey.set(key, nextVersion);
      }
      const h = card.querySelector('.home-score');
      const a = card.querySelector('.away-score');
      const newH = scoreText(g?.home?.score ?? g.home_score);
      const newA = scoreText(g?.away?.score ?? g.away_score);
      if (h) {
        if (h.textContent !== String(newH)) h.textContent = newH;
      }
      if (a) {
        if (a.textContent !== String(newA)) a.textContent = newA;
      }
      if (statusEl) {
        statusEl.innerHTML = renderStatusHTML(g);
        statusEl.dataset.status = g.status || '';
      }
      card.classList.toggle('live', isLive(g));
    });
  };

  async function refreshLive() {
    try {
      const data = await fetchFeed();
      RAW = data;
      patchCardsFromFeed(data);
    } catch (err) {
      console.warn('live refresh error', err);
    }
  }

  const getRefreshDelayMs = () => {
    const filtered = filterGames(Array.isArray(RAW) ? RAW : []);
    const hasLiveGames = filtered.some((game) => isLive(game));
    return hasLiveGames ? LIVE_GAME_REFRESH_MS : IDLE_REFRESH_MS;
  };

  const clearRefreshTimer = () => {
    if (refreshTimerId !== null) {
      window.clearTimeout(refreshTimerId);
      refreshTimerId = null;
    }
  };

  const scheduleNextRefresh = ({ immediate = false } = {}) => {
    clearRefreshTimer();
    if (document.hidden) return;
    const delay = immediate ? 0 : getRefreshDelayMs();
    refreshTimerId = window.setTimeout(runRefreshCycle, delay);
  };

  async function runRefreshCycle() {
    if (refreshInFlight) {
      scheduleNextRefresh();
      return;
    }
    refreshInFlight = true;
    try {
      await refreshLive();
    } finally {
      refreshInFlight = false;
      scheduleNextRefresh();
    }
  }

  const refreshButton = root.querySelector('.slb-btn[data-action="refresh"]');
  if (refreshButton) {
    refreshButton.addEventListener('click', () => {
      scheduleNextRefresh({ immediate: true });
    });
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearRefreshTimer();
      return;
    }
    scheduleNextRefresh({ immediate: true });
  });

  scheduleNextRefresh();

  window.addEventListener('message', (event) => {
    const payload = event?.data;
    if (!payload || payload.type !== EXTERNAL_FEED_UPDATE_EVENT || !Array.isArray(payload.games)) {
      return;
    }
    hasExternalFeedUpdate = true;
    RAW = payload.games;
    patchCardsFromFeed(payload.games);
    scheduleNextRefresh();
  });
  }

  // Wait for DOM to be ready, or initialize immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
</script>

`
    .replace('@@API_KEY@@', String(apiKey || ''))
    .replace('@@THEME_BG@@', resolvedTheme.bg)
    .replace('@@THEME_CARD@@', resolvedTheme.card)
    .replace('@@THEME_LINE@@', resolvedTheme.line)
    .replace('@@THEME_TEXT@@', resolvedTheme.text)
    .replace('@@THEME_MUTED@@', resolvedTheme.muted)
    .replace('@@THEME_ACCENT@@', resolvedTheme.accent)
    .replace('@@THEME_ACCENT_DARK@@', resolvedTheme.accentDark)
    .replace('@@THEME_CARD_HEADER_BG@@', resolvedTheme.cardHeaderBg)
    .replace('@@THEME_CARD_HEADER_TEXT@@', resolvedTheme.cardHeaderText)
    .replace('@@THEME_CARD_HEADER_STATUS@@', resolvedTheme.cardHeaderStatus)
    .replace('@@THEME_FONT_BASE@@', resolvedTheme.fontBase)
    .replace('@@THEME_FONT_TITLE@@', resolvedTheme.fontTitle)
    .replace('@@THEME_FONT_TEAM@@', resolvedTheme.fontTeam)
    .replace('@@THEME_HEADBAR_BG@@', resolvedTheme.headbarBg)
    .replace('@@THEME_HEADBAR_BORDER@@', resolvedTheme.headbarBorder)
    .replace('@@THEME_CONTROLS_LABEL@@', resolvedTheme.controlsLabel)
    .replace('@@THEME_CONTROLS_BG@@', resolvedTheme.controlsBg)
    .replace('@@THEME_CONTROLS_TEXT@@', resolvedTheme.controlsText)
    .replace('@@THEME_SCROLL_THUMB@@', resolvedTheme.scrollThumb)
    .replace('@@PRELOADED_GAMES@@', () => preloadedGamesJson)
    .replace('@@PREVIEW_USE_STATIC_FEED@@', preloadedGamesOnly ? 'true' : 'false')
    .trim();
};


const GamesBannerAdmin = () => {
  const { getIdToken } = useAuth();
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gamesError, setGamesError] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);
  const [keys, setKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keysError, setKeysError] = useState('');
  const [keyForm, setKeyForm] = useState(() => buildKeyForm(buildDefaultAllowedOrigins()));
  const [widgetRequests, setWidgetRequests] = useState([]);
  const [widgetRequestsLoading, setWidgetRequestsLoading] = useState(false);
  const [widgetRequestsError, setWidgetRequestsError] = useState('');
  const [widgetRequestActionId, setWidgetRequestActionId] = useState(null);
  const [widgetRequestNotes, setWidgetRequestNotes] = useState({});
  const [notification, setNotification] = useState('');
  const [activePage, setActivePage] = useState(ADMIN_PAGE.DASHBOARD);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [filterDataset, setFilterDataset] = useState(null);
  const [filterDatasetLoading, setFilterDatasetLoading] = useState(false);
  const [fullDatasetMode, setFullDatasetMode] = useState(false);
  const [filters, setFilters] = useState(() => buildDefaultGameFilters());
  const lastSelectedRef = useRef(null);
  const lastEditedFieldRef = useRef(null);
  const skipNextDraftSyncRef = useRef(false);
  const selectAllRef = useRef(null);
  const pageRef = useRef(1);
  const logoMapFileRef = useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvStatus, setCsvStatus] = useState('');
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvExporting, setCsvExporting] = useState(false);
  const [csvInputKey, setCsvInputKey] = useState(0);
  const [copiedKeyId, setCopiedKeyId] = useState(null);
  const copyIndicatorTimeoutRef = useRef(null);
  const [bulkDeleteState, setBulkDeleteState] = useState(null);
  const [externalListInput, setExternalListInput] = useState('');
  const [externalCompareError, setExternalCompareError] = useState('');
  const [externalCompareResults, setExternalCompareResults] = useState(null);
  const [logoMap, setLogoMap] = useState([{ name: '', url: '' }]);
  const [logoMapLoading, setLogoMapLoading] = useState(false);
  const [logoMapSaving, setLogoMapSaving] = useState(false);
  const [logoMapImporting, setLogoMapImporting] = useState(false);
  const [logoMapExporting, setLogoMapExporting] = useState(false);
  const [logoMapError, setLogoMapError] = useState('');
  const [previewGames, setPreviewGames] = useState([]);
  const [previewBootstrapGames, setPreviewBootstrapGames] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewIframeRef = useRef(null);
  const previewBootstrappedRef = useRef(false);

  const authorizedRequest = useCallback(async (url, options = {}) => {
    const token = await getIdToken();
    if (!token) {
      const err = new Error('A valid login is required for banner administration.');
      err.code = 'auth-required';
      throw err;
    }
    const headers = new Headers(options.headers || {});
    headers.set('Authorization', `Bearer ${token}`);
    return fetch(url, { ...options, headers });
  }, [getIdToken]);

  const loadGames = useCallback(async (requestedPage) => {
    const pageToLoad = Number.isFinite(Number(requestedPage)) && Number(requestedPage) > 0
      ? Number(requestedPage)
      : pageRef.current || 1;
    setGamesLoading(true);
    setGamesError('');
    try {
      const params = new URLSearchParams();
      params.set('includeDisabled', '1');
      params.set('limit', String(DEFAULT_PAGE_SIZE));
      params.set('page', String(pageToLoad));
      const response = await authorizedRequest(`${API_BASE}/games-admin/games?${params.toString()}`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load games');
      }
      setGames(Array.isArray(payload.games) ? payload.games : []);
      const rawPagination = payload.pagination || {};
      const rawLimit = Number(rawPagination.limit);
      const limit = Math.max(1, Number.isFinite(rawLimit) ? rawLimit : DEFAULT_PAGE_SIZE);
      const rawOffset = Number(rawPagination.offset);
      const offset = Math.max(0, Number.isFinite(rawOffset) ? rawOffset : (pageToLoad - 1) * limit);
      const rawTotal = Number(rawPagination.total);
      const total = Number.isFinite(rawTotal) && rawTotal >= 0
        ? rawTotal
        : offset + (Array.isArray(payload.games) ? payload.games.length : 0);
      const rawPage = Number(rawPagination.page);
      const derivedPage = Number.isFinite(rawPage) && rawPage > 0
        ? rawPage
        : Math.floor(offset / limit) + 1;
      const rawPageCount = Number(rawPagination.pageCount);
      const pageCount = Number.isFinite(rawPageCount) && rawPageCount > 0
        ? rawPageCount
        : Math.max(1, Math.ceil(total / limit));
      const hasMore = typeof rawPagination.hasMore === 'boolean'
        ? rawPagination.hasMore
        : offset + (Array.isArray(payload.games) ? payload.games.length : 0) < total;
      setPagination({
        limit,
        offset,
        total,
        page: derivedPage,
        pageCount,
        hasMore
      });
      pageRef.current = derivedPage;
    } catch (err) {
      setGamesError(err.message || 'Failed to load games');
    } finally {
      setGamesLoading(false);
    }
  }, [authorizedRequest]);

  const loadWidgetKeys = useCallback(async () => {
    setKeysLoading(true);
    setKeysError('');
    try {
      const response = await authorizedRequest(`${API_BASE}/games-admin/widget-keys`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load widget keys');
      }
      setKeys(Array.isArray(payload.keys) ? payload.keys : []);
    } catch (err) {
      setKeysError(err.message || 'Failed to load widget keys');
    } finally {
      setKeysLoading(false);
    }
  }, [authorizedRequest]);

  const loadWidgetRequests = useCallback(async () => {
    setWidgetRequestsLoading(true);
    setWidgetRequestsError('');
    try {
      const response = await authorizedRequest(`${API_BASE}/games-admin/widget-requests`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load widget requests');
      }
      setWidgetRequests(Array.isArray(payload.requests) ? payload.requests : []);
    } catch (err) {
      setWidgetRequestsError(err.message || 'Failed to load widget requests');
    } finally {
      setWidgetRequestsLoading(false);
    }
  }, [authorizedRequest]);

  const loadLogoMap = useCallback(async () => {
    setLogoMapLoading(true);
    setLogoMapError('');
    try {
      const response = await authorizedRequest(`${API_BASE}/games-admin/logo-map`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load logo mappings');
      }
      const rows = Array.isArray(payload.logos) ? payload.logos : [];
      setLogoMap(rows.length > 0 ? rows : [{ name: '', url: '' }]);
    } catch (err) {
      setLogoMapError(err.message || 'Failed to load logo mappings');
    } finally {
      setLogoMapLoading(false);
    }
  }, [authorizedRequest]);

  const pushPreviewFeedToIframe = useCallback((games) => {
    const targetWindow = previewIframeRef.current?.contentWindow;
    if (!targetWindow || !Array.isArray(games)) {
      return false;
    }
    targetWindow.postMessage({
      type: PREVIEW_FEED_MESSAGE_EVENT,
      games
    }, '*');
    return true;
  }, []);

  const handlePreviewIframeLoad = useCallback(() => {
    pushPreviewFeedToIframe(previewGames);
  }, [previewGames, pushPreviewFeedToIframe]);

  const loadPreviewFeed = useCallback(async ({ silent = false, forceRefresh = false } = {}) => {
    if (!silent) {
      setPreviewLoading(true);
    }
    setPreviewError('');
    try {
      const params = new URLSearchParams();
      if (forceRefresh) {
        params.set('refresh', '1');
      }
      const query = params.toString();
      const url = query
        ? `${API_BASE}/games-admin/banner-preview?${query}`
        : `${API_BASE}/games-admin/banner-preview`;
      const response = await authorizedRequest(url);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load preview feed');
      }
      const nextGames = Array.isArray(payload.games) ? payload.games : [];
      setPreviewGames(nextGames);
      if (!previewBootstrappedRef.current) {
        previewBootstrappedRef.current = true;
        setPreviewBootstrapGames(nextGames);
      } else {
        pushPreviewFeedToIframe(nextGames);
      }
    } catch (err) {
      setPreviewError(err.message || 'Failed to load preview feed');
      if (!silent) {
        setPreviewGames([]);
      }
    } finally {
      if (!silent) {
        setPreviewLoading(false);
      }
    }
  }, [authorizedRequest, pushPreviewFeedToIframe]);

  useEffect(() => {
    loadGames(1);
    loadWidgetKeys();
    loadWidgetRequests();
    loadLogoMap();
  }, [loadGames, loadLogoMap, loadWidgetKeys, loadWidgetRequests]);

  useEffect(() => {
    if (activePage !== ADMIN_PAGE.DASHBOARD) {
      return undefined;
    }
    loadPreviewFeed({ silent: true });
    const intervalId = window.setInterval(() => {
      loadPreviewFeed({ silent: true });
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, [activePage, loadPreviewFeed]);

  const sortedDrafts = useMemo(() => {
    return [...drafts].sort((a, b) => {
      const aTime = toUnixMillis(a.values.tipoff) ?? Infinity;
      const bTime = toUnixMillis(b.values.tipoff) ?? Infinity;
      if (aTime !== bTime) return aTime - bTime;
      return String(a.values.competition || '').localeCompare(String(b.values.competition || ''));
    });
  }, [drafts]);

  const dirtyDraftKeys = useMemo(() => {
    const dirty = new Set();
    drafts.forEach((draft) => {
      if (draftHasUnsavedChanges(draft)) {
        dirty.add(draft.key);
      }
    });
    return dirty;
  }, [drafts]);

  const filteredDrafts = useMemo(() => {
    const tipoffStartMillis = parseIsoDateBoundaryMillis(filters.tipoffStart);
    const tipoffEndMillis = parseIsoDateBoundaryMillis(filters.tipoffEnd, { endOfDay: true });
    const normalizedCompetition = filters.competition.trim().toLowerCase();
    const normalizedHome = filters.home.trim().toLowerCase();
    const normalizedAway = filters.away.trim().toLowerCase();
    const normalizedTeam = filters.team.trim().toLowerCase();
    return sortedDrafts.filter((draft) => {
      const tipoffMillis = toUnixMillis(draft.values.tipoff);
      if (tipoffStartMillis !== null) {
        if (tipoffMillis === null || tipoffMillis < tipoffStartMillis) return false;
      }
      if (tipoffEndMillis !== null) {
        if (tipoffMillis === null || tipoffMillis > tipoffEndMillis) return false;
      }
      if (normalizedCompetition && !String(draft.values.competition || '').toLowerCase().includes(normalizedCompetition)) {
        return false;
      }
      if (normalizedHome) {
        const homeName = String(draft.values.home_name || '').toLowerCase();
        if (!homeName.includes(normalizedHome)) {
          return false;
        }
      }
      if (normalizedAway) {
        const awayName = String(draft.values.away_name || '').toLowerCase();
        if (!awayName.includes(normalizedAway)) {
          return false;
        }
      }
      if (normalizedTeam) {
        const homeName = String(draft.values.home_name || '').toLowerCase();
        const awayName = String(draft.values.away_name || '').toLowerCase();
        const matchesTeam = homeName.includes(normalizedTeam)
          || awayName.includes(normalizedTeam);
        if (!matchesTeam) {
          return false;
        }
      }
      if (filters.status && draft.values.status !== filters.status) {
        return false;
      }
      return true;
    });
  }, [filters, sortedDrafts]);

  const dashboardPreviewSrcDoc = useMemo(() => {
    return WIDGET_SNIPPET('', buildBannerFeedUrl(), {}, {
      preloadedGames: previewBootstrapGames,
      preloadedGamesOnly: true
    });
  }, [previewBootstrapGames]);

  const filtersActive = Boolean(
    filters.tipoffStart
    || filters.tipoffEnd
    || filters.competition.trim()
    || filters.home.trim()
    || filters.away.trim()
    || filters.team.trim()
    || filters.status
  );

  const dateRangeSummary = useMemo(() => {
    return buildDateRangeSummary(filters.tipoffStart, filters.tipoffEnd);
  }, [filters.tipoffEnd, filters.tipoffStart]);

  const filteredCount = filteredDrafts.length;

  const dirtyDrafts = useMemo(() => {
    return sortedDrafts.filter((draft) => dirtyDraftKeys.has(draft.key));
  }, [dirtyDraftKeys, sortedDrafts]);

  const dirtyDraftCount = dirtyDrafts.length;
  const dirtySaving = dirtyDrafts.some((draft) => draft.saving);

  const bulkDeleteInProgress = Boolean(bulkDeleteState?.active);
  const bulkDeleteTotal = bulkDeleteState?.total || 0;
  const bulkDeleteCompleted = bulkDeleteState ? Math.min(bulkDeleteState.completed, bulkDeleteTotal) : 0;
  const bulkDeletePercent = bulkDeleteTotal > 0 ? Math.round((bulkDeleteCompleted / bulkDeleteTotal) * 100) : 0;

  const selectedCount = selectedKeys.size;
  const disableSaveChanges = dirtyDraftCount === 0 || dirtySaving || bulkDeleteInProgress;
  const disableResetChanges = dirtyDraftCount === 0 || dirtySaving || bulkDeleteInProgress;
  const deleteDisabled = selectedCount === 0 || bulkDeleteInProgress;
  const compactHeaderStyle = { whiteSpace: 'nowrap', width: '1%', paddingRight: 12 };
  const compactCellStyle = { whiteSpace: 'nowrap', width: '1%', paddingRight: 12, verticalAlign: 'top' };
  const filterLabelStyle = { fontSize: 11, textTransform: 'uppercase', color: '#666', marginBottom: 4 };
  const selectHeaderStyle = {
    ...compactHeaderStyle,
    textAlign: 'center',
    width: 62,
    paddingRight: 0
  };
  const selectCellStyle = {
    ...compactCellStyle,
    textAlign: 'center',
    width: 62,
    paddingRight: 0,
    paddingTop: 8
  };
  const checkboxWrapStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 18
  };
  const linksCellStyle = {
    minWidth: 300,
    verticalAlign: 'top',
    paddingTop: 6
  };
  const linksGroupStyle = {
    display: 'grid',
    gap: 6,
    padding: '8px 10px',
    border: '1px solid #d9dfe8',
    borderRadius: 6,
    background: '#f8fbff'
  };
  const linksFieldStyle = {
    display: 'grid',
    gap: 2
  };
  const linksFieldLabelStyle = {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.25,
    color: '#5f6f82'
  };
  const gamesHeaderCellStyle = {
    verticalAlign: 'top',
    paddingTop: 0
  };
  const pageTabsWrapStyle = {
    marginBottom: 20,
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'center'
  };
  const pageTabButtonStyle = (pageKey) => ({
    padding: '8px 12px',
    borderRadius: 8,
    border: activePage === pageKey ? '1px solid #0f2135' : '1px solid #cfd8e3',
    background: activePage === pageKey ? '#0f2135' : '#fff',
    color: activePage === pageKey ? '#fff' : '#24364a',
    cursor: 'pointer',
    fontWeight: 600
  });
  const pageIntroText = activePage === ADMIN_PAGE.DASHBOARD
    ? 'Dashboard shows widget preview, embed request review, and widget key management.'
    : activePage === ADMIN_PAGE.GAMES
      ? 'Games includes CSV import/export, external compare, and game editing.'
      : 'Logo mappings are managed on this page.';

  const ensureFilterDataset = useCallback(async ({ showAlert = false } = {}) => {
    if (filterDataset !== null) {
      return filterDataset;
    }
    setGamesError('');
    setFilterDatasetLoading(true);
    try {
      const aggregated = [];
      let pageToFetch = 1;
      while (true) {
        const params = new URLSearchParams();
        params.set('includeDisabled', '1');
        params.set('limit', String(DEFAULT_PAGE_SIZE));
        params.set('page', String(pageToFetch));
        // eslint-disable-next-line no-await-in-loop
        const response = await authorizedRequest(`${API_BASE}/games-admin/games?${params.toString()}`);
        // eslint-disable-next-line no-await-in-loop
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || 'Failed to load games');
        }
        if (Array.isArray(payload.games)) {
          aggregated.push(...payload.games);
        }
        const paginationInfo = payload.pagination || {};
        const continueFetching = paginationInfo.hasMore
          || (paginationInfo.pageCount && paginationInfo.page && paginationInfo.page < paginationInfo.pageCount);
        if (!continueFetching) {
          break;
        }
        pageToFetch += 1;
      }
      setFilterDataset(aggregated);
      return aggregated;
    } catch (err) {
      const message = err.message || 'Failed to load all games';
      if (showAlert) {
        window.alert(message);
      } else {
        setGamesError(message);
      }
      return null;
    } finally {
      setFilterDatasetLoading(false);
    }
  }, [authorizedRequest, filterDataset]);

  useEffect(() => {
    if (skipNextDraftSyncRef.current) {
      skipNextDraftSyncRef.current = false;
      return;
    }
    const shouldUseFilterDataset = (fullDatasetMode || filtersActive) && filterDataset !== null;
    const waitingForFilterDataset = activePage === ADMIN_PAGE.GAMES && filtersActive && filterDataset === null;
    const sourceGames = shouldUseFilterDataset
      ? filterDataset
      : (waitingForFilterDataset ? [] : games);
    const sortedGames = [...sourceGames].sort((a, b) => {
      const aTime = new Date(a.tipoff || 0).getTime();
      const bTime = new Date(b.tipoff || 0).getTime();
      return aTime - bTime;
    });
    setDrafts(sortedGames.map(mapGameToDraft));
    setSelectedKeys((prev) => {
      const next = new Set();
      sortedGames.forEach((game) => {
        const key = String(game.id);
        if (prev.has(key)) next.add(key);
      });
      return next;
    });
  }, [activePage, filterDataset, filtersActive, fullDatasetMode, games]);

  useEffect(() => {
    if (activePage !== ADMIN_PAGE.GAMES || !filtersActive || filterDataset !== null || filterDatasetLoading) {
      return;
    }
    ensureFilterDataset();
  }, [activePage, ensureFilterDataset, filterDataset, filterDatasetLoading, filtersActive]);

  const visibleSelection = useMemo(() => {
    if (filteredDrafts.length === 0) {
      return { count: 0, allSelected: false, someSelected: false };
    }
    let count = 0;
    filteredDrafts.forEach((draft) => {
      if (selectedKeys.has(draft.key)) count += 1;
    });
    const allSelected = count === filteredDrafts.length && filteredDrafts.length > 0;
    const someSelected = count > 0 && !allSelected;
    return { count, allSelected, someSelected };
  }, [filteredDrafts, selectedKeys]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    if (filteredDrafts.length === 0) {
      selectAllRef.current.indeterminate = false;
      selectAllRef.current.checked = false;
      return;
    }
    if (visibleSelection.allSelected) {
      selectAllRef.current.indeterminate = false;
      selectAllRef.current.checked = true;
    } else if (visibleSelection.someSelected) {
      selectAllRef.current.indeterminate = true;
      selectAllRef.current.checked = false;
    } else {
      selectAllRef.current.indeterminate = false;
      selectAllRef.current.checked = false;
    }
  }, [filteredDrafts.length, visibleSelection]);

  useEffect(() => () => {
    if (copyIndicatorTimeoutRef.current) {
      clearTimeout(copyIndicatorTimeoutRef.current);
    }
  }, []);

  const notify = useCallback((message) => {
    setNotification(message);
    if (message) {
      window.setTimeout(() => setNotification(''), 5000);
    }
  }, []);

  const getDraftFieldProps = useCallback((draftKey, field) => ({
    id: buildDraftFieldId(draftKey, field),
    'data-draft-key': String(draftKey),
    'data-draft-field': field
  }), []);

  const handleDraftFieldFocusCapture = useCallback((event) => {
    const draftKey = event?.target?.dataset?.draftKey;
    const field = event?.target?.dataset?.draftField;
    if (!draftKey || !field) return;
    lastEditedFieldRef.current = { draftKey: String(draftKey), field: String(field) };
  }, []);

  const restoreDraftFieldFocus = useCallback((draftKey, field) => {
    if (!draftKey || !field || typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    const targetId = buildDraftFieldId(draftKey, field);
    let attempts = 0;
    const tryFocus = () => {
      const element = document.getElementById(targetId);
      if (!element) {
        if (attempts < 8) {
          attempts += 1;
          window.setTimeout(tryFocus, 16);
        }
        return;
      }
      try {
        element.focus({ preventScroll: true });
      } catch (err) {
        element.focus();
      }
      if (typeof element.setSelectionRange === 'function' && typeof element.value === 'string') {
        const cursorPos = element.value.length;
        element.setSelectionRange(cursorPos, cursorPos);
      }
    };
    window.requestAnimationFrame(tryFocus);
  }, []);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const resetDateRangeFilter = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      tipoffStart: getDefaultTipoffStartDate(),
      tipoffEnd: ''
    }));
  }, []);

  const clearDateRangeFilter = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      tipoffStart: '',
      tipoffEnd: ''
    }));
  }, []);

  const handleDraftChange = useCallback((key, field, value) => {
    lastEditedFieldRef.current = { draftKey: String(key), field: String(field) };
    setDrafts((prev) => prev.map((draft) => {
      if (draft.key !== key) {
        return draft;
      }
      return {
        ...draft,
        values: {
          ...draft.values,
          [field]: value
        }
      };
    }));
  }, []);

  const handleTipoffPartChange = useCallback((key, part, value) => {
    lastEditedFieldRef.current = { draftKey: String(key), field: `tipoff_${String(part)}` };
    setDrafts((prev) => prev.map((draft) => {
      if (draft.key !== key) return draft;
      const parts = splitTipoffInput(draft.values.tipoff);
      const nextParts = {
        date: part === 'date' ? value : parts.date,
        time: part === 'time' ? value : parts.time
      };
      const nextTipoff = buildTipoffFromParts(nextParts.date, nextParts.time);
      return {
        ...draft,
        values: {
          ...draft.values,
          tipoff: nextTipoff
        }
      };
    }));
  }, []);

  const persistLogoMap = useCallback(async (entries) => {
    setLogoMapSaving(true);
    setLogoMapError('');
    try {
      const response = await authorizedRequest(`${API_BASE}/games-admin/logo-map`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logos: entries })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to save logo mappings');
      }
      notify('Logo mappings saved.');
    } catch (err) {
      setLogoMapError(err.message || 'Failed to save logo mappings');
    } finally {
      setLogoMapSaving(false);
    }
  }, [authorizedRequest, notify]);

  const saveLogoMap = useCallback(() => {
    persistLogoMap(logoMap);
  }, [logoMap, persistLogoMap]);

  const handleLogoMapChange = useCallback((index, field, value) => {
    setLogoMap((prev) => prev.map((row, idx) => {
      if (idx !== index) return row;
      return { ...row, [field]: value };
    }));
  }, []);

  const addLogoMapRow = useCallback(() => {
    setLogoMap((prev) => [...prev, { name: '', url: '' }]);
  }, []);

  const removeLogoMapRow = useCallback((index) => {
    setLogoMap((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const runExternalComparison = useCallback(() => {
    setExternalCompareError('');
    try {
      const rawItems = parseExternalListInput(externalListInput);
      const externalGames = rawItems.map(mapExternalRecord).filter((item) => item.home_name || item.away_name);
      if (externalGames.length === 0) {
        setExternalCompareResults(null);
        setExternalCompareError('No games found in the pasted list. Include columns like competition, tipoff, home_name, away_name.');
        return;
      }
      const appGames = drafts.map((draft) => ({
        competition: draft.values.competition,
        tipoff: draft.values.tipoff,
        home_name: draft.values.home_name,
        away_name: draft.values.away_name,
        key: draft.key
      }));
      const appMap = new Map();
      appGames.forEach((game) => {
        const key = buildMatchKey(game);
        if (key) appMap.set(key, game);
      });
      const externalMap = new Map();
      externalGames.forEach((game) => {
        const key = buildMatchKey(game);
        if (key) externalMap.set(key, game);
      });
      const missingInApp = [];
      const extraInApp = [];
      externalMap.forEach((value, key) => {
        if (!appMap.has(key)) {
          missingInApp.push(value);
        }
      });
      appMap.forEach((value, key) => {
        if (!externalMap.has(key)) {
          extraInApp.push(value);
        }
      });
      setExternalCompareResults({
        totalExternal: externalGames.length,
        totalApp: appGames.length,
        missingInApp,
        extraInApp
      });
    } catch (err) {
      setExternalCompareResults(null);
      setExternalCompareError(err.message || 'Failed to parse external list.');
    }
  }, [drafts, externalListInput]);

  const applyExternalStatusScores = useCallback(() => {
    setExternalCompareError('');
    try {
      const rawItems = parseExternalListInput(externalListInput);
      const externalGames = rawItems.map((record) => {
        const mapped = mapExternalRecord(record);
        const status = normalizeExternalStatus(record?.status);
        const scoreRaw = record?.score || record?.final_score || record?.result || '';
        const score = parseScorePair(scoreRaw);
        return {
          ...mapped,
          status,
          score
        };
      }).filter((item) => item.home_name || item.away_name);
      if (externalGames.length === 0) {
        setExternalCompareError('No games found in the pasted list. Include columns like competition, date, time, home_name, away_name.');
        return;
      }
      const externalMap = new Map();
      externalGames.forEach((game) => {
        const key = buildMatchKey(game);
        if (key) externalMap.set(key, game);
      });
      let updated = 0;
      setDrafts((prev) => prev.map((draft) => {
        const key = buildMatchKey(draft.values);
        const match = externalMap.get(key);
        if (!match) return draft;
        const nextValues = { ...draft.values };
        if (match.status) {
          nextValues.status = match.status;
        }
        if (match.score.home) {
          nextValues.home_score = match.score.home;
        }
        if (match.score.away) {
          nextValues.away_score = match.score.away;
        }
        const didChange = nextValues.status !== draft.values.status
          || nextValues.home_score !== draft.values.home_score
          || nextValues.away_score !== draft.values.away_score;
        if (!didChange) return draft;
        updated += 1;
        return { ...draft, values: nextValues };
      }));
      notify(`Applied status/score updates to ${updated} game${updated === 1 ? '' : 's'}.`);
    } catch (err) {
      setExternalCompareError(err.message || 'Failed to parse external list.');
    }
  }, [externalListInput, notify]);

  const handleAddRow = useCallback(() => {
    setDrafts((prev) => [...prev, createEmptyDraft()]);
  }, []);

  const handleKeyInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setKeyForm((prev) => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleCsvFileChange = useCallback((event) => {
    setCsvFile(event.target.files?.[0] || null);
    setCsvStatus('');
    setCsvErrors([]);
  }, []);

  const triggerCsvDownload = useCallback((blob, fallbackFilename, response) => {
    if (!blob) return;
    let filename = fallbackFilename || 'games.csv';
    const disposition =
      typeof response?.headers?.get === 'function' ? response.headers.get('Content-Disposition') : '';
    if (disposition) {
      let match = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
      if (match && match[1]) {
        const candidate = match[1].replace(/(^"|"$)/g, '');
        try {
          filename = decodeURIComponent(candidate);
        } catch (err) {
          filename = candidate;
        }
      } else {
        match = disposition.match(/filename="?([^"]+)"?/i);
        if (match && match[1]) {
          filename = match[1];
        }
      }
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || fallbackFilename || 'games.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  const handleExportLogoMap = useCallback(() => {
    setLogoMapExporting(true);
    try {
      const csv = buildLogoMapCsv(logoMap);
      const blob = new Blob([csv], { type: 'text/csv; charset=utf-8' });
      const fallbackName = `team-logo-map-${new Date().toISOString().slice(0, 10)}.csv`;
      triggerCsvDownload(blob, fallbackName);
      notify('Logo mappings exported.');
    } catch (err) {
      const message = err.message || 'Failed to export logo mappings';
      setLogoMapError(message);
      window.alert(message);
    } finally {
      setLogoMapExporting(false);
    }
  }, [logoMap, notify, triggerCsvDownload]);

  const triggerLogoMapImport = useCallback(() => {
    if (logoMapFileRef.current) {
      logoMapFileRef.current.click();
    }
  }, []);

  const handleLogoMapImport = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLogoMapImporting(true);
    setLogoMapError('');
    try {
      const text = await file.text();
      const parsed = parseLogoMapText(text);
      if (parsed.length === 0) {
        throw new Error('No logo mappings found in the file.');
      }
      setLogoMap(parsed);
      await persistLogoMap(parsed);
    } catch (err) {
      setLogoMapError(err.message || 'Failed to import logo mappings');
    } finally {
      setLogoMapImporting(false);
      event.target.value = '';
    }
  }, [persistLogoMap]);

  const useFullDataset = fullDatasetMode && filterDataset !== null;
  const fullDatasetCount = filterDataset ? filterDataset.length : 0;
  const currentPage = pagination?.page ?? pageRef.current ?? 1;
  const totalPages = pagination?.pageCount ?? 1;
  const baseTotalGames = useFullDataset
    ? fullDatasetCount
    : (typeof pagination?.total === 'number' ? pagination.total : games.length);
  const paginationLimit = useFullDataset
    ? Math.max(fullDatasetCount, 1)
    : (pagination?.limit || DEFAULT_PAGE_SIZE);
  const offset = useFullDataset ? 0 : (pagination?.offset ?? ((currentPage - 1) * paginationLimit));
  const showingStart = baseTotalGames === 0 ? 0 : (useFullDataset ? 1 : Math.min(offset + 1, baseTotalGames));
  const showingEnd = baseTotalGames === 0
    ? 0
    : (useFullDataset ? baseTotalGames : Math.min(offset + games.length, baseTotalGames));
  const canGoPrev = !useFullDataset && currentPage > 1 && !gamesLoading;
  const canGoNext = !useFullDataset && !gamesLoading && Boolean(pagination?.hasMore || currentPage < totalPages);
  const singlePageFilteredView = useFullDataset || filtersActive;
  const displayShowingStart = filtersActive ? (filteredCount === 0 ? 0 : 1) : showingStart;
  const displayShowingEnd = filtersActive ? filteredCount : showingEnd;
  const displayTotalGames = filtersActive ? filteredCount : baseTotalGames;
  const displayCurrentPage = singlePageFilteredView ? 1 : currentPage;
  const displayTotalPages = singlePageFilteredView ? 1 : Math.max(totalPages, 1);
  const displayCanGoPrev = singlePageFilteredView ? false : canGoPrev;
  const displayCanGoNext = singlePageFilteredView ? false : canGoNext;

  const goToPrevPage = useCallback(() => {
    if (!displayCanGoPrev) return;
    const prevPage = Math.max(1, (pagination?.page ?? pageRef.current ?? 1) - 1);
    loadGames(prevPage);
  }, [displayCanGoPrev, loadGames, pagination]);

  const goToNextPage = useCallback(() => {
    if (!displayCanGoNext) return;
    const nextPage = (pagination?.page ?? pageRef.current ?? 1) + 1;
    if (!pagination?.hasMore && pagination?.pageCount && nextPage > pagination.pageCount) {
      return;
    }
    loadGames(nextPage);
  }, [displayCanGoNext, loadGames, pagination]);

  const loadAllGames = useCallback(async () => {
    const dataset = await ensureFilterDataset({ showAlert: true });
    if (!dataset) return;
    setFullDatasetMode(true);
    setSelectedKeys(new Set());
    notify(`Loaded all ${dataset.length} games.`);
  }, [ensureFilterDataset, notify]);

  const exitFullDatasetMode = useCallback(() => {
    setFullDatasetMode(false);
    setSelectedKeys(new Set());
    loadGames(pageRef.current || 1);
    notify('Returned to paginated view.');
  }, [loadGames, notify]);

  const renderPaginationControls = (position) => {
    if (!pagination && !filtersActive && !useFullDataset) return null;
    const spacingStyles = position === 'bottom'
      ? { marginTop: 12 }
      : { marginBottom: 12 };
    return (
      <div
        key={`pager-${position}`}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          ...spacingStyles
        }}
      >
        <div style={{ fontSize: 13, color: '#555' }}>
          {displayTotalGames === 0
            ? 'No games found.'
            : `Showing ${displayShowingStart}-${displayShowingEnd} of ${displayTotalGames} · Page ${displayCurrentPage} of ${displayTotalPages}`}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={goToPrevPage} disabled={!displayCanGoPrev}>
            ← Previous
          </button>
          <button type="button" onClick={goToNextPage} disabled={!displayCanGoNext}>
            Next →
          </button>
        </div>
      </div>
    );
  };

  const saveDrafts = useCallback(async (draftsToSave) => {
    const targets = draftsToSave.filter((draft) => draftHasUnsavedChanges(draft));
    if (targets.length === 0) return;
    const focusTarget = lastEditedFieldRef.current;
    const targetKeys = new Set(targets.map((draft) => draft.key));
    setDrafts((prev) => prev.map((entry) => {
      if (!targetKeys.has(entry.key)) return entry;
      return { ...entry, saving: true, error: '' };
    }));
    const errors = [];
    let successCount = 0;
    let createdCount = 0;
    const savedGames = [];
    const keyRemap = new Map();
    for (const draft of targets) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const payload = buildGamePayload(draft.values);
        const endpoint = draft.isNew
          ? `${API_BASE}/games-admin/games`
          : `${API_BASE}/games-admin/games/${draft.record.id}`;
        const method = draft.isNew ? 'POST' : 'PATCH';
        // eslint-disable-next-line no-await-in-loop
        const response = await authorizedRequest(endpoint, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        // eslint-disable-next-line no-await-in-loop
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body.error || 'Failed to save game');
        }
        const savedGame = body?.game;
        if (!savedGame || savedGame.id === null || typeof savedGame.id === 'undefined') {
          throw new Error('Failed to save game');
        }
        const nextKey = String(savedGame.id);
        keyRemap.set(draft.key, nextKey);
        if (draft.isNew && draft.key !== nextKey) {
          createdCount += 1;
        }
        savedGames.push(savedGame);
        setDrafts((prev) => prev.map((entry) => {
          if (entry.key !== draft.key) return entry;
          return mapGameToDraft(savedGame);
        }));
        successCount += 1;
      } catch (err) {
        const message = err.message || 'Failed to save game';
        errors.push(message);
        setDrafts((prev) => prev.map((entry) => {
          if (entry.key !== draft.key) return entry;
          return { ...entry, error: message };
        }));
      } finally {
        const mappedKey = keyRemap.get(draft.key);
        setDrafts((prev) => prev.map((entry) => {
          if (entry.key !== draft.key && (!mappedKey || entry.key !== mappedKey)) return entry;
          return { ...entry, saving: false };
        }));
      }
    }
    if (savedGames.length > 0) {
      skipNextDraftSyncRef.current = true;
      setGames((prev) => mergeGamesById(prev, savedGames));
      setFilterDataset((prev) => (Array.isArray(prev) ? mergeGamesById(prev, savedGames) : prev));
      if (createdCount > 0) {
        setPagination((prev) => {
          if (!prev) return prev;
          const prevTotal = Number(prev.total);
          const limit = Number(prev.limit);
          if (!Number.isFinite(prevTotal)) return prev;
          const nextTotal = prevTotal + createdCount;
          const nextPageCount = Number.isFinite(limit) && limit > 0
            ? Math.max(1, Math.ceil(nextTotal / limit))
            : prev.pageCount;
          return {
            ...prev,
            total: nextTotal,
            pageCount: nextPageCount
          };
        });
      }
      if (keyRemap.size > 0) {
        setSelectedKeys((prev) => {
          if (prev.size === 0) return prev;
          let changed = false;
          const next = new Set();
          prev.forEach((key) => {
            if (keyRemap.has(key)) {
              next.add(keyRemap.get(key));
              changed = true;
              return;
            }
            next.add(key);
          });
          return changed ? next : prev;
        });
        if (lastSelectedRef.current && keyRemap.has(lastSelectedRef.current)) {
          lastSelectedRef.current = keyRemap.get(lastSelectedRef.current);
        }
      }
    }
    if (successCount > 0) {
      notify(successCount === 1 ? 'Game saved.' : `Saved ${successCount} games.`);
    }
    if (focusTarget?.draftKey && focusTarget?.field) {
      const mappedDraftKey = keyRemap.get(String(focusTarget.draftKey)) || String(focusTarget.draftKey);
      restoreDraftFieldFocus(mappedDraftKey, String(focusTarget.field));
    }
    if (errors.length > 0) {
      const first = errors[0];
      const prefix = errors.length > 1 ? `Failed to save ${errors.length} game(s). ` : '';
      window.alert(`${prefix}${first}`);
    }
  }, [authorizedRequest, notify, restoreDraftFieldFocus]);

  const handleSaveChanges = useCallback(() => {
    if (dirtyDraftCount === 0) return;
    saveDrafts(dirtyDrafts);
  }, [dirtyDraftCount, dirtyDrafts, saveDrafts]);

  const toggleSelection = useCallback((key, checked, shiftKey) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (shiftKey && lastSelectedRef.current) {
        const visibleKeys = filteredDrafts.map((draft) => draft.key);
        const lastIndex = visibleKeys.indexOf(lastSelectedRef.current);
        const currentIndex = visibleKeys.indexOf(key);
        if (lastIndex !== -1 && currentIndex !== -1) {
          const [start, end] = currentIndex > lastIndex
            ? [lastIndex, currentIndex]
            : [currentIndex, lastIndex];
          for (let i = start; i <= end; i += 1) {
            const targetKey = visibleKeys[i];
            if (checked) {
              next.add(targetKey);
            } else {
              next.delete(targetKey);
            }
          }
          lastSelectedRef.current = key;
          return next;
        }
      }
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      lastSelectedRef.current = key;
      return next;
    });
  }, [filteredDrafts, lastSelectedRef]);

  const handleSelectAll = useCallback((checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      filteredDrafts.forEach((draft) => {
        if (checked) {
          next.add(draft.key);
        } else {
          next.delete(draft.key);
        }
      });
      return next;
    });
  }, [filteredDrafts]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.size === 0) return;
    const draftsToDelete = sortedDrafts.filter((draft) => selectedKeys.has(draft.key));
    if (draftsToDelete.length === 0) return;
    if (!window.confirm(`Delete ${draftsToDelete.length} selected game(s)?`)) {
      return;
    }
    const selectedSet = new Set(draftsToDelete.map((draft) => draft.key));
    setDrafts((prev) => prev.filter((entry) => !selectedSet.has(entry.key)));
    setBulkDeleteState({ active: true, total: draftsToDelete.length, completed: 0 });
    const errors = [];
    for (const draft of draftsToDelete) {
      if (draft.isNew || !draft.record?.id) {
        setBulkDeleteState((prev) => (prev ? { ...prev, completed: prev.completed + 1 } : prev));
        // eslint-disable-next-line no-continue
        continue;
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await authorizedRequest(`${API_BASE}/games-admin/games/${draft.record.id}`, {
          method: 'DELETE'
        });
        // eslint-disable-next-line no-await-in-loop
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || 'Failed to delete game');
        }
      } catch (err) {
        errors.push(err.message || 'Failed to delete game');
      } finally {
        setBulkDeleteState((prev) => (prev ? { ...prev, completed: prev.completed + 1 } : prev));
      }
    }
    await loadGames();
    setBulkDeleteState(null);
    setSelectedKeys(new Set());
    if (errors.length > 0) {
      const errorMessage = errors[0];
      window.alert(`Some games could not be deleted (${errors.length}). ${errorMessage}`);
    } else {
      notify(`Deleted ${draftsToDelete.length} game(s).`);
    }
  }, [authorizedRequest, loadGames, notify, selectedKeys, sortedDrafts]);

  const handleResetChanges = useCallback(() => {
    if (dirtyDraftCount === 0) return;
    const draftsToReset = dirtyDrafts;
    const keysToRemove = new Set();
    const recordsToRestore = new Map();
    draftsToReset.forEach((draft) => {
      if (draft.isNew || !draft.record) {
        keysToRemove.add(draft.key);
      } else {
        recordsToRestore.set(draft.key, draft.record);
      }
    });
    setDrafts((prev) => {
      const next = [];
      prev.forEach((entry) => {
        if (keysToRemove.has(entry.key)) {
          return;
        }
        if (recordsToRestore.has(entry.key)) {
          next.push(mapGameToDraft(recordsToRestore.get(entry.key)));
          return;
        }
        next.push(entry);
      });
      return next;
    });
    if (keysToRemove.size > 0) {
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        keysToRemove.forEach((key) => next.delete(key));
        return next;
      });
    }
    notify(draftsToReset.length === 1 ? 'Reset 1 row.' : `Reset ${draftsToReset.length} rows.`);
  }, [dirtyDraftCount, dirtyDrafts, notify]);

  const refreshBannerCache = useCallback(async () => {
    try {
      const response = await authorizedRequest(`${API_BASE}/games-admin/cache/refresh`, { method: 'POST' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to refresh cache');
      }
      setCacheStatus(payload);
      notify('Banner cache refreshed.');
    } catch (err) {
      window.alert(err.message || 'Failed to refresh banner cache');
    }
  }, [authorizedRequest, notify]);

  const updateWidgetRequestRow = useCallback((nextRequest) => {
    if (!nextRequest || !nextRequest.id) {
      return;
    }
    setWidgetRequests((prev) => prev.map((item) => (item.id === nextRequest.id ? nextRequest : item)));
  }, []);

  const handleWidgetRequestNoteChange = useCallback((requestId, value) => {
    setWidgetRequestNotes((prev) => ({
      ...prev,
      [requestId]: value
    }));
  }, []);

  const approveWidgetRequestRequest = useCallback(async (request) => {
    if (!request?.id || request.status !== 'pending') {
      return;
    }
    if (!window.confirm('Approve this request and create a widget key now?')) {
      return;
    }
    setWidgetRequestActionId(request.id);
    try {
      const reviewNotes = widgetRequestNotes[request.id] || '';
      const response = await authorizedRequest(`${API_BASE}/games-admin/widget-requests/${request.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_notes: reviewNotes })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to approve request');
      }
      if (payload.request) {
        updateWidgetRequestRow(payload.request);
      }
      if (payload.key) {
        setKeys((prev) => [payload.key, ...prev.filter((key) => key.id !== payload.key.id)]);
      }
      const emailStatus = payload?.email_notification?.status;
      const emailSuffix = emailStatus === 'sent'
        ? ' Approval email sent.'
        : emailStatus === 'failed'
          ? ' Approval email failed (see server logs).'
          : emailStatus === 'skipped'
            ? ' Approval email skipped (SMTP not configured).'
            : '';
      notify(`Request approved and widget key created.${emailSuffix}`);
    } catch (err) {
      window.alert(err.message || 'Failed to approve request');
    } finally {
      setWidgetRequestActionId(null);
    }
  }, [authorizedRequest, notify, updateWidgetRequestRow, widgetRequestNotes]);

  const rejectWidgetRequestRequest = useCallback(async (request) => {
    if (!request?.id || request.status !== 'pending') {
      return;
    }
    if (!window.confirm('Reject this embed request?')) {
      return;
    }
    setWidgetRequestActionId(request.id);
    try {
      const reviewNotes = widgetRequestNotes[request.id] || '';
      const response = await authorizedRequest(`${API_BASE}/games-admin/widget-requests/${request.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ review_notes: reviewNotes })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to reject request');
      }
      if (payload.request) {
        updateWidgetRequestRow(payload.request);
      }
      notify('Request rejected.');
    } catch (err) {
      window.alert(err.message || 'Failed to reject request');
    } finally {
      setWidgetRequestActionId(null);
    }
  }, [authorizedRequest, notify, updateWidgetRequestRow, widgetRequestNotes]);

  const deleteWidgetRequestRequest = useCallback(async (request) => {
    if (!request?.id) {
      return;
    }
    if (!window.confirm('Delete this widget request? If approved, its embed key will also be removed.')) {
      return;
    }
    setWidgetRequestActionId(request.id);
    try {
      const response = await authorizedRequest(`${API_BASE}/games-admin/widget-requests/${request.id}`, {
        method: 'DELETE'
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to delete request');
      }
      setWidgetRequests((prev) => prev.filter((item) => item.id !== request.id));
      if (payload.deleted_key_id) {
        setKeys((prev) => prev.filter((key) => key.id !== payload.deleted_key_id));
      }
      notify(payload.deleted_key_id ? 'Request and widget key deleted.' : 'Request deleted.');
    } catch (err) {
      window.alert(err.message || 'Failed to delete request');
    } finally {
      setWidgetRequestActionId(null);
    }
  }, [authorizedRequest, notify]);

  const handleSaveWidgetKey = useCallback(async (event) => {
    event.preventDefault();
    try {
      const payload = {
        label: keyForm.label,
        domain: keyForm.domain,
        allowed_origins: keyForm.allowed_origins,
        allowed_competitions: keyForm.allowed_competitions,
        status: keyForm.status
      };
      const response = await authorizedRequest(`${API_BASE}/games-admin/widget-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || 'Failed to create widget key');
      }
      setKeys((prev) => [result.key, ...prev]);
      setKeyForm(buildKeyForm(buildDefaultAllowedOrigins()));
      notify('Widget key created.');
    } catch (err) {
      window.alert(err.message || 'Failed to create widget key');
    }
  }, [authorizedRequest, keyForm, notify]);

  const updateWidgetKey = useCallback(async (keyId, changes) => {
    try {
      const response = await authorizedRequest(`${API_BASE}/games-admin/widget-keys/${keyId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changes)
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to update widget key');
      }
      setKeys((prev) => prev.map((key) => (key.id === keyId ? payload.key : key)));
      notify('Widget key updated.');
    } catch (err) {
      window.alert(err.message || 'Failed to update widget key');
    }
  }, [authorizedRequest, notify]);

  const rotateWidgetKey = useCallback(async (keyId) => {
    if (!window.confirm('Rotate this API key? Embedded sites must update immediately.')) {
      return;
    }
    try {
      const response = await authorizedRequest(`${API_BASE}/games-admin/widget-keys/${keyId}/rotate`, {
        method: 'POST'
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to rotate widget key');
      }
      setKeys((prev) => prev.map((key) => (key.id === keyId ? payload.key : key)));
      notify('Widget key rotated.');
    } catch (err) {
      window.alert(err.message || 'Failed to rotate widget key');
    }
  }, [authorizedRequest, notify]);

  const deleteWidgetKey = useCallback(async (keyId) => {
    if (!window.confirm('Delete this widget key? Existing embeds using it will stop working.')) {
      return;
    }
    try {
      const response = await authorizedRequest(`${API_BASE}/games-admin/widget-keys/${keyId}`, {
        method: 'DELETE'
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to delete widget key');
      }
      setKeys((prev) => prev.filter((key) => key.id !== keyId));
      notify('Widget key deleted.');
    } catch (err) {
      window.alert(err.message || 'Failed to delete widget key');
    }
  }, [authorizedRequest, notify]);

  const waitForCsvImportJob = useCallback(async (jobId) => {
    const safeJobId = String(jobId || '').trim();
    if (!safeJobId) {
      throw new Error('Import job id is missing.');
    }
    const maxAttempts = 900;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const pollParams = new URLSearchParams();
      pollParams.set('_', String(Date.now()));
      const response = await authorizedRequest(
        `${API_BASE}/games-admin/import-csv/jobs/${encodeURIComponent(safeJobId)}?${pollParams.toString()}`,
        { cache: 'no-store' }
      );
      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response, 'Failed to check CSV import status'));
      }
      const payload = await response.json().catch(() => ({}));
      const job = payload?.job || {};
      const status = String(job.status || '').toLowerCase();
      if (status === 'completed') {
        return job;
      }
      if (status === 'failed') {
        throw new Error(job.error || job.message || 'CSV import failed.');
      }
      const progress = job?.stats || {};
      const progressLabel = Number.isFinite(Number(progress.processed))
        ? `processed ${progress.processed || 0}, created ${progress.created || 0}, updated ${progress.updated || 0}, skipped ${progress.skipped || 0}`
        : `${attempt + 1}/${maxAttempts}`;
      setCsvStatus(job.message || `Import in progress… ${progressLabel}`);
      await sleep(2000);
    }
    throw new Error('CSV import is taking too long. Please try a smaller file or check server logs.');
  }, [authorizedRequest]);

  const handleImportCsv = useCallback(async (event) => {
    event.preventDefault();
    if (!csvFile) {
      window.alert('Select a CSV file first.');
      return;
    }
    setCsvUploading(true);
    setCsvStatus('');
    setCsvErrors([]);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const response = await authorizedRequest(`${API_BASE}/games-admin/import-csv?async=1`, {
        method: 'POST',
        body: formData
      });
      if (!response.ok) {
        throw new Error(await getResponseErrorMessage(response, 'Failed to import CSV'));
      }
      const payload = await response.json().catch(() => ({}));
      const queuedJob = payload?.job || {};
      const queuedStatus = String(queuedJob.status || '').toLowerCase();
      let finalJob = queuedJob;
      if (response.status === 202 || queuedStatus === 'queued' || queuedStatus === 'running') {
        setCsvStatus(queuedJob.message || 'Import queued. Processing...');
        finalJob = await waitForCsvImportJob(queuedJob.id);
      }
      const stats = finalJob?.stats || payload.stats || {};
      const summary = `CSV import complete — created ${stats.created || 0}, updated ${stats.updated || 0}, skipped ${stats.skipped || 0}.`;
      setCsvStatus(summary);
      setCsvErrors(Array.isArray(stats.errors) ? stats.errors : []);
      notify(summary);
      setCsvFile(null);
      setCsvInputKey((prev) => prev + 1);
      await loadGames();
    } catch (err) {
      window.alert(err.message || 'Failed to import CSV');
    } finally {
      setCsvUploading(false);
    }
  }, [authorizedRequest, csvFile, loadGames, notify, waitForCsvImportJob]);

  const handleExportCsv = useCallback(async () => {
    setCsvExporting(true);
    setCsvStatus('Preparing export…');
    setCsvErrors([]);
    try {
      const response = await authorizedRequest(`${API_BASE}/games-admin/export-csv`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to export CSV');
      }
      const blob = await response.blob();
      const fallbackName = `games-banner-export-${new Date().toISOString().slice(0, 10)}.csv`;
      triggerCsvDownload(blob, fallbackName, response);
      setCsvStatus('Export downloaded.');
      notify('Export downloaded.');
    } catch (err) {
      const message = err.message || 'Failed to export CSV';
      setCsvStatus(`Export error: ${message}`);
      window.alert(message);
    } finally {
      setCsvExporting(false);
    }
  }, [authorizedRequest, notify, triggerCsvDownload]);

  const copyEmbedCode = useCallback((keyId, apiKey) => {
    const snippet = WIDGET_SNIPPET(apiKey, buildBannerFeedUrl());
    const markCopied = () => {
      notify('Embed code copied to clipboard.');
      setCopiedKeyId(keyId);
      if (copyIndicatorTimeoutRef.current) {
        clearTimeout(copyIndicatorTimeoutRef.current);
      }
      copyIndicatorTimeoutRef.current = setTimeout(() => {
        setCopiedKeyId(null);
      }, 2000);
    };
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(snippet).then(() => {
        markCopied();
      }).catch(() => {
        window.prompt('Copy the embed code:', snippet);
        markCopied();
      });
    } else {
      window.prompt('Copy the embed code:', snippet);
      markCopied();
    }
  }, [notify]);

  return (
    <div className="admin-panel" style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <h1>Games Banner Admin</h1>
      {notification && (
        <div style={{ background: '#e0f2ff', border: '1px solid #90caf9', padding: '8px 12px', borderRadius: 6, marginBottom: 16 }}>
          {notification}
        </div>
      )}

      <section style={{ marginBottom: 24 }}>
        <div style={pageTabsWrapStyle}>
          <button type="button" style={pageTabButtonStyle(ADMIN_PAGE.DASHBOARD)} onClick={() => setActivePage(ADMIN_PAGE.DASHBOARD)}>
            Dashboard
          </button>
          <button type="button" style={pageTabButtonStyle(ADMIN_PAGE.GAMES)} onClick={() => setActivePage(ADMIN_PAGE.GAMES)}>
            Games
          </button>
          <button type="button" style={pageTabButtonStyle(ADMIN_PAGE.LOGOS)} onClick={() => setActivePage(ADMIN_PAGE.LOGOS)}>
            Logo mappings
          </button>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: '#59697d' }}>{pageIntroText}</p>
      </section>

      {activePage === ADMIN_PAGE.GAMES && (
      <section style={{ marginBottom: 32 }}>
        <h2>Import games from CSV</h2>
        <p style={{ fontSize: 13, color: '#555' }}>
          Include columns such as <code>competition</code>, <code>status</code>, <code>home_name</code>, <code>away_name</code>, <code>tipoff</code> (ISO or date+time), or separate <code>date</code> + <code>time</code>. Logo URLs are managed separately in team logo mappings.
        </p>
        <form onSubmit={handleImportCsv} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <input
            key={csvInputKey}
            type="file"
            accept=".csv,text/csv"
            onChange={handleCsvFileChange}
          />
          <button type="submit" disabled={!csvFile || csvUploading}>
            {csvUploading ? 'Uploading…' : 'Upload CSV'}
          </button>
          <button type="button" onClick={handleExportCsv} disabled={csvExporting || csvUploading}>
            {csvExporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </form>
        {csvStatus && <p style={{ marginTop: 8, color: '#1565c0' }}>{csvStatus}</p>}
        {csvErrors.length > 0 && (
          <div style={{ marginTop: 8, color: '#b71c1c', fontSize: 13 }}>
            <div>Some rows were skipped:</div>
            <ul>
              {csvErrors.slice(0, 5).map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        )}
      </section>
      )}

      {activePage === ADMIN_PAGE.GAMES && (
      <section style={{ marginBottom: 32 }}>
        <h2>External compare (safe preview)</h2>
        <p style={{ fontSize: 13, color: '#555' }}>
          Paste a CSV or JSON list of games to compare with the current drafts. Accepts <code>tipoff</code> or separate <code>date</code> + <code>time</code>. This does not change your data.
        </p>
        <textarea
          value={externalListInput}
          onChange={(e) => setExternalListInput(e.target.value)}
          placeholder="Paste CSV or JSON here. Columns: competition, tipoff OR date + time, home_name, away_name"
          rows={6}
          style={{ width: '100%', maxWidth: 900, fontFamily: 'monospace', fontSize: 12 }}
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
          <button type="button" onClick={runExternalComparison}>Compare lists</button>
          <button type="button" onClick={applyExternalStatusScores}>Apply status/score to drafts</button>
          <button type="button" onClick={() => { setExternalListInput(''); setExternalCompareResults(null); setExternalCompareError(''); }}>
            Clear
          </button>
        </div>
        {externalCompareError && <p style={{ color: 'crimson', marginTop: 8 }}>{externalCompareError}</p>}
        {externalCompareResults && (
          <div style={{ marginTop: 12, background: '#f8f8f8', border: '1px solid #eee', padding: 12, borderRadius: 8 }}>
            <p style={{ marginTop: 0, fontSize: 13 }}>
              External games: {externalCompareResults.totalExternal} · Your drafts: {externalCompareResults.totalApp}
            </p>
            <div style={{ display: 'grid', gap: 8 }}>
              <div>
                <strong>Missing in your list ({externalCompareResults.missingInApp.length})</strong>
                {externalCompareResults.missingInApp.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#2e7d32' }}>None</div>
                ) : (
                  <ul style={{ marginTop: 6, fontSize: 13 }}>
                    {externalCompareResults.missingInApp.slice(0, 20).map((game, idx) => (
                      <li key={`missing-${idx}`}>{formatMatchLabel(game)}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <strong>Extra in your list ({externalCompareResults.extraInApp.length})</strong>
                {externalCompareResults.extraInApp.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#2e7d32' }}>None</div>
                ) : (
                  <ul style={{ marginTop: 6, fontSize: 13 }}>
                    {externalCompareResults.extraInApp.slice(0, 20).map((game, idx) => (
                      <li key={`extra-${idx}`}>{formatMatchLabel(game)}</li>
                    ))}
                  </ul>
                )}
              </div>
              {(externalCompareResults.missingInApp.length > 20 || externalCompareResults.extraInApp.length > 20) && (
                <div style={{ fontSize: 12, color: '#555' }}>
                  Only the first 20 items in each list are shown.
                </div>
              )}
            </div>
          </div>
        )}
      </section>
      )}

      {activePage === ADMIN_PAGE.LOGOS && (
      <section style={{ marginBottom: 32 }}>
        <h2>Team logo mappings</h2>
        <p style={{ fontSize: 13, color: '#555' }}>
          Maintain a simple name → logo URL map. These mappings are saved and used as a fallback for the widget when a game is missing logo URLs. Import/export uses a CSV with <code>team_name</code> and <code>logo_url</code> columns.
        </p>
        {logoMapLoading && <p style={{ fontSize: 12, color: '#777' }}>Loading logo mappings…</p>}
        {logoMapError && <p style={{ fontSize: 12, color: 'crimson' }}>{logoMapError}</p>}
        <div style={{ display: 'grid', gap: 8 }}>
          {logoMap.map((row, idx) => (
            <div key={`logo-${idx}`} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                placeholder="Team name"
                value={row.name}
                onChange={(e) => handleLogoMapChange(idx, 'name', e.target.value)}
                style={{ minWidth: 220 }}
              />
              <input
                placeholder="Logo URL"
                value={row.url}
                onChange={(e) => handleLogoMapChange(idx, 'url', e.target.value)}
                style={{ minWidth: 360 }}
              />
              <button type="button" onClick={() => removeLogoMapRow(idx)}>Remove</button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
          <button type="button" onClick={addLogoMapRow}>Add mapping</button>
          <button type="button" onClick={saveLogoMap} disabled={logoMapSaving || logoMapLoading}>
            {logoMapSaving ? 'Saving mappings…' : 'Save mappings'}
          </button>
          <button type="button" onClick={handleExportLogoMap} disabled={logoMapExporting || logoMapLoading}>
            {logoMapExporting ? 'Exporting…' : 'Export mappings'}
          </button>
          <button type="button" onClick={triggerLogoMapImport} disabled={logoMapImporting || logoMapLoading}>
            {logoMapImporting ? 'Importing…' : 'Import mappings'}
          </button>
          <input
            ref={logoMapFileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleLogoMapImport}
            style={{ display: 'none' }}
          />
        </div>
      </section>
      )}

      {activePage === ADMIN_PAGE.GAMES && (
      <section style={{ marginBottom: 32 }}>
        <header
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            background: '#fff',
            padding: '12px 0',
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            borderBottom: '1px solid #e0e0e0',
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.05)'
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Games (oldest → newest)</h2>
            {cacheStatus && (
              <p style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
                Cache version {cacheStatus.version} · {cacheStatus.count} games
              </p>
            )}
            {dirtyDraftCount > 0 && (
              <p style={{ fontSize: 13, color: '#af5200', marginTop: 4 }}>
                {dirtyDraftCount} row{dirtyDraftCount === 1 ? '' : 's'} have unsaved changes.
              </p>
            )}
            {selectedCount > 0 && (
              <p style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
                {selectedCount} row{selectedCount === 1 ? '' : 's'} selected for delete.
              </p>
            )}
            {!filterDataset && !filterDatasetLoading && (
              <p style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                Filters automatically load all games and then search every game.
              </p>
            )}
            {filterDatasetLoading && !filterDataset && (
              <p style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                Loading all games for filters…
              </p>
            )}
            {filterDataset && (
              <p style={{ fontSize: 12, color: '#2e7d32', marginTop: 4 }}>
                Full dataset loaded — filters search all {filterDataset.length} games.
              </p>
            )}
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <label style={{ fontSize: 12, textTransform: 'uppercase', color: '#555' }}>
                Team (any)
                <input
                  value={filters.team}
                  onChange={(e) => handleFilterChange('team', e.target.value)}
                  placeholder="Filter home or away team"
                  style={{ marginLeft: 6, minWidth: 220 }}
                />
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" onClick={handleAddRow}>+ Add row</button>
            <button type="button" onClick={loadGames} disabled={gamesLoading}>Reload</button>
            <button type="button" onClick={refreshBannerCache}>Refresh cache</button>
            {useFullDataset ? (
              <button type="button" onClick={exitFullDatasetMode} disabled={filterDatasetLoading}>
                Return to paged view
              </button>
            ) : (
              <button type="button" onClick={loadAllGames} disabled={filterDatasetLoading || gamesLoading}>
                {filterDatasetLoading ? 'Loading all…' : 'Load all games'}
              </button>
            )}
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={disableSaveChanges}
            >
              {dirtySaving
                ? 'Saving…'
                : dirtyDraftCount > 0
                  ? `Save Changes (${dirtyDraftCount})`
                  : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={handleResetChanges}
              disabled={disableResetChanges}
            >
              {dirtyDraftCount > 0 ? `Reset Changes (${dirtyDraftCount})` : 'Reset Changes'}
            </button>
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={deleteDisabled}
            >
              {bulkDeleteInProgress
                ? 'Deleting…'
                : selectedCount > 0
                  ? `Delete selected (${selectedCount})`
                  : 'Delete selected'}
            </button>
          </div>
        </header>
        {renderPaginationControls('top')}
        {bulkDeleteInProgress && (
          <div style={{ marginBottom: 12, background: '#e3f2fd', border: '1px solid #bbdefb', borderRadius: 6, padding: '8px 12px' }}>
            <div style={{ fontSize: 13, color: '#0d47a1', marginBottom: 6 }}>
              Deleting games… {bulkDeleteCompleted}/{bulkDeleteTotal}
            </div>
            <div style={{ height: 6, background: '#bbdefb', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: '#1e88e5', width: `${bulkDeletePercent}%`, transition: 'width 160ms ease-out' }} />
            </div>
          </div>
        )}
        {gamesError && <p style={{ color: 'crimson' }}>{gamesError}</p>}
        {gamesLoading ? (
          <p>Loading games…</p>
        ) : (
          <div
            style={{
              width: '100vw',
              marginLeft: 'calc(50% - 50vw)',
              marginRight: 'calc(50% - 50vw)',
              padding: '0 32px 32px',
              boxSizing: 'border-box',
              overflowX: 'auto'
            }}
          >
            <table
              onFocusCapture={handleDraftFieldFocusCapture}
              style={{
                width: '100%',
                minWidth: 1100,
                maxWidth: 'calc(100vw - 64px)',
                margin: '0 auto',
                borderCollapse: 'collapse',
                fontSize: 14
              }}
            >
              <thead>
                <tr>
                  <th style={{ ...selectHeaderStyle, ...gamesHeaderCellStyle }}>
                    <div style={filterLabelStyle}>Select</div>
                    <div style={checkboxWrapStyle}>
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={filteredDrafts.length > 0 && visibleSelection.allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        disabled={filteredDrafts.length === 0}
                      />
                    </div>
                  </th>
                  <th align="left" style={{ ...compactHeaderStyle, ...gamesHeaderCellStyle }}>
                    <div style={filterLabelStyle}>Tip-off range</div>
                    <details style={{ minWidth: 250, position: 'relative' }}>
                      <summary
                        style={{
                          cursor: 'pointer',
                          listStyle: 'none',
                          border: '1px solid #d0d7de',
                          borderRadius: 6,
                          padding: '6px 8px',
                          background: '#fff',
                          userSelect: 'none'
                        }}
                      >
                        {dateRangeSummary}
                      </summary>
                      <div
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 6px)',
                          left: 0,
                          zIndex: 30,
                          minWidth: 250,
                          display: 'grid',
                          gap: 8,
                          padding: 10,
                          border: '1px solid #d0d7de',
                          borderRadius: 8,
                          background: '#fff',
                          boxShadow: '0 12px 24px rgba(0, 0, 0, 0.12)'
                        }}
                      >
                        <label style={{ display: 'grid', gap: 4, fontSize: 12, color: '#555' }}>
                          From
                          <input
                            type="date"
                            value={filters.tipoffStart}
                            onChange={(e) => handleFilterChange('tipoffStart', e.target.value)}
                            style={{ width: '100%' }}
                          />
                        </label>
                        <label style={{ display: 'grid', gap: 4, fontSize: 12, color: '#555' }}>
                          To
                          <input
                            type="date"
                            value={filters.tipoffEnd}
                            onChange={(e) => handleFilterChange('tipoffEnd', e.target.value)}
                            style={{ width: '100%' }}
                          />
                        </label>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button type="button" onClick={resetDateRangeFilter}>
                            Last 7 days onward
                          </button>
                          <button type="button" onClick={clearDateRangeFilter}>
                            Clear
                          </button>
                        </div>
                      </div>
                    </details>
                  </th>
                  <th align="left" style={{ ...compactHeaderStyle, ...gamesHeaderCellStyle }}>
                    <div style={filterLabelStyle}>Competition</div>
                    <input
                      value={filters.competition}
                      onChange={(e) => handleFilterChange('competition', e.target.value)}
                      placeholder="Filter competition"
                      style={{ width: '100%' }}
                    />
                  </th>
                  <th align="left" style={gamesHeaderCellStyle}>
                    <div style={filterLabelStyle}>Home team</div>
                    <input
                      value={filters.home}
                      onChange={(e) => handleFilterChange('home', e.target.value)}
                      placeholder="Filter home"
                      style={{ width: '100%' }}
                    />
                  </th>
                  <th align="left" style={gamesHeaderCellStyle}>
                    <div style={filterLabelStyle}>Away team</div>
                    <input
                      value={filters.away}
                      onChange={(e) => handleFilterChange('away', e.target.value)}
                      placeholder="Filter away"
                      style={{ width: '100%' }}
                    />
                  </th>
                  <th align="left" style={{ ...compactHeaderStyle, ...gamesHeaderCellStyle }}>
                    <div style={filterLabelStyle}>Status</div>
                    <select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <option value="">All</option>
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </th>
                  <th align="left" style={{ ...compactHeaderStyle, ...gamesHeaderCellStyle }}>
                    <div style={filterLabelStyle}>Links</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredDrafts.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '16px 0' }}>
                      {drafts.length === 0 ? 'No games saved yet.' : 'No games match the current filters.'}
                    </td>
                  </tr>
                )}
                {filteredDrafts.map((draft) => {
                  const rowIsDirty = dirtyDraftKeys.has(draft.key);
                  const tipoffParts = splitTipoffInput(draft.values.tipoff);
                  return (
                    <Fragment key={draft.key}>
                      <tr
                        style={{
                          borderTop: '1px solid #eee',
                          verticalAlign: 'top',
                          background: rowIsDirty ? '#fff8e1' : 'transparent',
                          transition: 'background-color 160ms ease-out'
                        }}
                      >
                        <td style={selectCellStyle}>
                          <div style={checkboxWrapStyle}>
                            <input
                              type="checkbox"
                              checked={selectedKeys.has(draft.key)}
                              onChange={(e) => toggleSelection(draft.key, e.target.checked, Boolean(e.nativeEvent?.shiftKey))}
                            />
                          </div>
                        </td>
                        <td style={compactCellStyle}>
                          <div style={{ display: 'grid', gap: 6 }}>
                            <input
                              {...getDraftFieldProps(draft.key, 'tipoff_date')}
                              placeholder="dd/mm/yyyy"
                              value={tipoffParts.date}
                              onChange={(e) => handleTipoffPartChange(draft.key, 'date', e.target.value)}
                              style={{ width: '130px' }}
                              required
                            />
                            <input
                              {...getDraftFieldProps(draft.key, 'tipoff_time')}
                              placeholder="hh:mm"
                              value={tipoffParts.time}
                              onChange={(e) => handleTipoffPartChange(draft.key, 'time', e.target.value)}
                              style={{ width: '90px' }}
                              required
                            />
                          </div>
                        </td>
                        <td style={compactCellStyle}>
                          <input
                            {...getDraftFieldProps(draft.key, 'competition')}
                            value={draft.values.competition}
                            onChange={(e) => handleDraftChange(draft.key, 'competition', e.target.value)}
                            style={{ width: '160px' }}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'grid', gap: 4 }}>
                            <input
                              {...getDraftFieldProps(draft.key, 'home_name')}
                              placeholder="Name"
                              value={draft.values.home_name}
                              onChange={(e) => handleDraftChange(draft.key, 'home_name', e.target.value)}
                            />
                            <input
                              {...getDraftFieldProps(draft.key, 'home_score')}
                              placeholder="Score"
                              value={draft.values.home_score}
                              onChange={(e) => handleDraftChange(draft.key, 'home_score', e.target.value)}
                            />
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'grid', gap: 4 }}>
                            <input
                              {...getDraftFieldProps(draft.key, 'away_name')}
                              placeholder="Name"
                              value={draft.values.away_name}
                              onChange={(e) => handleDraftChange(draft.key, 'away_name', e.target.value)}
                            />
                            <input
                              {...getDraftFieldProps(draft.key, 'away_score')}
                              placeholder="Score"
                              value={draft.values.away_score}
                              onChange={(e) => handleDraftChange(draft.key, 'away_score', e.target.value)}
                            />
                          </div>
                        </td>
                        <td style={compactCellStyle}>
                          <div style={{ display: 'grid', gap: 6 }}>
                            <select
                              {...getDraftFieldProps(draft.key, 'status')}
                              value={draft.values.status}
                              onChange={(e) => handleDraftChange(draft.key, 'status', e.target.value)}
                            >
                              {STATUS_OPTIONS.map((status) => (
                                <option key={status} value={status}>{status}</option>
                              ))}
                            </select>
                            <textarea
                              {...getDraftFieldProps(draft.key, 'teaser')}
                              placeholder="Teaser / notes"
                              value={draft.values.teaser}
                              onChange={(e) => handleDraftChange(draft.key, 'teaser', e.target.value)}
                              rows={2}
                            />
                          </div>
                        </td>
                        <td style={linksCellStyle}>
                          <div style={linksGroupStyle}>
                            <label style={linksFieldStyle}>
                              <span style={linksFieldLabelStyle}>Watch URL</span>
                              <input
                                {...getDraftFieldProps(draft.key, 'watch_url')}
                                placeholder="Watch URL"
                                value={draft.values.watch_url}
                                onChange={(e) => handleDraftChange(draft.key, 'watch_url', e.target.value)}
                              />
                            </label>
                            <label style={linksFieldStyle}>
                              <span style={linksFieldLabelStyle}>Highlights URL</span>
                              <input
                                {...getDraftFieldProps(draft.key, 'link_url')}
                                placeholder="Highlights URL"
                                value={draft.values.link_url}
                                onChange={(e) => handleDraftChange(draft.key, 'link_url', e.target.value)}
                              />
                            </label>
                            <label style={linksFieldStyle}>
                              <span style={linksFieldLabelStyle}>Ticket URL</span>
                              <input
                                {...getDraftFieldProps(draft.key, 'ticket_url')}
                                placeholder="Ticket URL"
                                value={draft.values.ticket_url}
                                onChange={(e) => handleDraftChange(draft.key, 'ticket_url', e.target.value)}
                              />
                            </label>
                          </div>
                        </td>
                      </tr>
                      {(draft.error || draft.saving) && (
                        <tr>
                          <td
                            colSpan={7}
                            style={{
                              padding: '4px 12px 10px',
                              fontSize: 12,
                              color: draft.error ? 'crimson' : '#1565c0',
                              background: rowIsDirty ? '#fff4d1' : '#f8f8f8'
                            }}
                          >
                            {draft.error || 'Saving…'}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!gamesLoading && renderPaginationControls('bottom')}
      </section>
      )}

      {activePage === ADMIN_PAGE.DASHBOARD && (
      <>
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 12 }}>Widget Preview</h2>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: 13, color: '#555', marginTop: 0, marginBottom: 16 }}>
            This shows how the banner widget will appear when embedded on your website with the current games data.
          </p>
          {previewLoading && (
            <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
              Loading preview…
            </div>
          )}
          {previewError && (
            <div style={{ padding: 12, background: '#fff4f4', borderRadius: 8, color: '#8b1c1c', marginBottom: 12 }}>
              {previewError}
            </div>
          )}
          {!previewLoading && !previewError && previewGames.length === 0 && (
            <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 8, textAlign: 'center', color: '#777', marginBottom: 12 }}>
              No displayable games are currently available for the widget feed.
            </div>
          )}
          <iframe
            ref={previewIframeRef}
            title="Widget Preview"
            srcDoc={dashboardPreviewSrcDoc}
            onLoad={handlePreviewIframeLoad}
            style={{ width: '100%', border: 'none', height: 240, minHeight: 240, overflow: 'hidden', display: 'block' }}
          />
        </div>
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2>Embed requests</h2>
        <p style={{ fontSize: 13, color: '#555', marginTop: 0 }}>
          Requests from the public widget studio. Approving creates an active widget key automatically.
        </p>
        <div style={{ marginBottom: 10 }}>
          <button
            type="button"
            onClick={loadWidgetRequests}
            disabled={widgetRequestsLoading || widgetRequestActionId !== null}
          >
            {widgetRequestsLoading ? 'Loading…' : 'Reload requests'}
          </button>
        </div>
        {widgetRequestsError && <p style={{ color: 'crimson' }}>{widgetRequestsError}</p>}
        {widgetRequestsLoading ? (
          <p>Loading embed requests…</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th align="left">Submitted</th>
                  <th align="left">Requester</th>
                  <th align="left">Website</th>
                  <th align="left">Use case</th>
                  <th align="left">Status</th>
                  <th align="left">Review</th>
                </tr>
              </thead>
              <tbody>
                {widgetRequests.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '16px 0' }}>
                      No embed requests submitted yet.
                    </td>
                  </tr>
                )}
                {widgetRequests.map((request) => {
                  const isPending = request.status === 'pending';
                  const isActing = widgetRequestActionId === request.id;
                  const statusStyle = request.status === 'approved'
                    ? { color: '#1f6b2d', background: '#e6f4ea', borderColor: '#b7dfbf' }
                    : request.status === 'rejected'
                      ? { color: '#8a1c1c', background: '#fdecec', borderColor: '#f0bcbc' }
                      : { color: '#7a5a00', background: '#fff7e0', borderColor: '#ecd08f' };
                  return (
                    <tr key={request.id} style={{ borderTop: '1px solid #eee', verticalAlign: 'top' }}>
                      <td>
                        {request.created_at ? new Date(request.created_at).toLocaleString() : '—'}
                      </td>
                      <td>
                        <div>{request.requester_name || '—'}</div>
                        <div style={{ color: '#555' }}>{request.requester_email || '—'}</div>
                      </td>
                      <td>
                        {request.website_url ? (
                          <a href={request.website_url} target="_blank" rel="noreferrer">{request.website_url}</a>
                        ) : '—'}
                        {request.embed_page_url && (
                          <div style={{ marginTop: 4, color: '#555' }}>
                            Page:{' '}
                            <a href={request.embed_page_url} target="_blank" rel="noreferrer">{request.embed_page_url}</a>
                          </div>
                        )}
                      </td>
                      <td style={{ maxWidth: 340 }}>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{request.use_case || '—'}</div>
                        {Array.isArray(request.requested_competitions) && request.requested_competitions.length > 0 && (
                          <div style={{ marginTop: 6, color: '#555' }}>
                            Competitions: {request.requested_competitions.join(', ')}
                          </div>
                        )}
                        {request.theme && (
                          <details style={{ marginTop: 6 }}>
                            <summary style={{ cursor: 'pointer', color: '#1a4a7a' }}>Theme payload</summary>
                            <pre style={{ marginTop: 6, fontSize: 11, maxHeight: 140, overflow: 'auto', background: '#f7f9fc', padding: 8 }}>
                              {JSON.stringify(request.theme, null, 2)}
                            </pre>
                          </details>
                        )}
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 999,
                          border: '1px solid',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          ...statusStyle
                        }}
                        >
                          {request.status || 'pending'}
                        </span>
                        {request.reviewed_by && (
                          <div style={{ marginTop: 6, color: '#555' }}>
                            by {request.reviewed_by}
                          </div>
                        )}
                        {request.reviewed_at && (
                          <div style={{ color: '#555' }}>
                            {new Date(request.reviewed_at).toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td style={{ minWidth: 220 }}>
                        {isPending ? (
                          <div style={{ display: 'grid', gap: 6 }}>
                            <textarea
                              rows={2}
                              placeholder="Optional review note"
                              value={widgetRequestNotes[request.id] || ''}
                              onChange={(e) => handleWidgetRequestNoteChange(request.id, e.target.value)}
                              disabled={isActing}
                            />
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button type="button" onClick={() => approveWidgetRequestRequest(request)} disabled={isActing}>
                                {isActing ? 'Processing…' : 'Approve + create key'}
                              </button>
                              <button type="button" onClick={() => rejectWidgetRequestRequest(request)} disabled={isActing}>
                                Reject
                              </button>
                              <button type="button" onClick={() => deleteWidgetRequestRequest(request)} disabled={isActing}>
                                Delete
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gap: 6 }}>
                            <div style={{ color: '#555' }}>{request.admin_notes || 'No review note.'}</div>
                            <div>
                              <button type="button" onClick={() => deleteWidgetRequestRequest(request)} disabled={isActing}>
                                {isActing ? 'Processing…' : 'Delete request'}
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2>Widget keys</h2>
        {keysError && <p style={{ color: 'crimson' }}>{keysError}</p>}
        {keysLoading ? (
          <p>Loading widget keys…</p>
        ) : (
          <div style={{ overflowX: 'auto', marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th align="left">Label</th>
                  <th align="left">Domain</th>
                  <th align="left">Origins</th>
                  <th align="left">Competitions</th>
                  <th align="left">Status</th>
                  <th align="left">API key</th>
                  <th align="left">Last used</th>
                  <th align="left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '16px 0' }}>No widget keys created.</td>
                  </tr>
                )}
                {keys.map((key) => (
                  <tr key={key.id} style={{ borderTop: '1px solid #eee' }}>
                    <td>{key.label || '—'}</td>
                    <td>{key.domain || '—'}</td>
                    <td>{key.allowedOrigins?.join(', ') || '—'}</td>
                    <td>{key.allowedCompetitions?.join(', ') || 'All competitions'}</td>
                    <td>{key.status}</td>
                    <td style={{ fontFamily: 'monospace' }}>{key.api_key}</td>
                    <td>{key.last_used_at ? new Date(key.last_used_at).toLocaleString() : '—'}</td>
                    <td style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button type="button" onClick={() => rotateWidgetKey(key.id)}>Rotate</button>
                      <button type="button" onClick={() => updateWidgetKey(key.id, { status: key.status === 'active' ? 'inactive' : 'active' })}>
                        {key.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                      <button type="button" onClick={() => deleteWidgetKey(key.id)}>Delete</button>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button type="button" onClick={() => copyEmbedCode(key.id, key.api_key)}>Copy embed</button>
                        {copiedKeyId === key.id && (
                          <span style={{ fontSize: 12, color: '#2e7d32' }}>Copied!</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form onSubmit={handleSaveWidgetKey} style={{ display: 'grid', gap: 12, maxWidth: 600 }}>
          <h3>Create widget key</h3>
          <label>
            <span>Label</span>
            <input name="label" value={keyForm.label} onChange={handleKeyInputChange} required />
          </label>
          <label>
            <span>Domain / client</span>
            <input name="domain" value={keyForm.domain} onChange={handleKeyInputChange} />
          </label>
          <label>
            <span>Allowed origins (comma separated)</span>
            <input name="allowed_origins" value={keyForm.allowed_origins} onChange={handleKeyInputChange} placeholder="https://allbasketball.co.uk,https://courts.allbasketball.co.uk" />
          </label>
          <label>
            <span>Allowed competitions (comma separated, optional)</span>
            <input name="allowed_competitions" value={keyForm.allowed_competitions} onChange={handleKeyInputChange} placeholder="SLB Championship,SLB Cup" />
          </label>
          <label>
            <span>Status</span>
            <select name="status" value={keyForm.status} onChange={handleKeyInputChange}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
          <button type="submit">Create key</button>
        </form>
      </section>
      </>
      )}
    </div>
  );
};

export default GamesBannerAdmin;
